import { useState, useEffect, Children, cloneElement, useRef, useMemo } from 'react'
import { Modal, Button, Container, Row, Col, Form, FloatingLabel, InputGroup, FormControl, FormSelect, ButtonGroup, ToggleButton, Table, Tabs, Tab, Badge, Spinner, Alert } from 'react-bootstrap'
import { Calendar, momentLocalizer, Views } from 'react-big-calendar'
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop'
import TimeGrid from 'react-big-calendar/lib/TimeGrid'
import moment from 'moment'
import 'moment/locale/cs'
import Notification from '../components/Notification'
import { useNavigate } from 'react-router-dom'
import { forceLogout, shouldForceLogout } from '../services/auth/session'
import { useAutoRefresh } from '../hooks/useAutoRefresh'
import { fetchRezervaceCiselniky, fetchRezervaceDetail, fetchRezervaceHistorie, searchRezervace, sendRezervaceEmail, sendRezervaceSms, updateRezervaceStav } from '../services/api/rezervace'
import carOsIcon from '../assets/images/icons/car-size-os.png'
import carSuvIcon from '../assets/images/icons/car-size-suv.png'
import carVanIcon from '../assets/images/icons/car-size-van.png'
import carTruckIcon from '../assets/images/icons/car-size-truck.png'
import carBikeIcon from '../assets/images/icons/car-size-bike.png'
import defectIcon from '../assets/images/icons/defekt.png'
import balanceIcon from '../assets/images/icons/vyvazeni.png'
import tireDiskIcon from '../assets/images/icons/pneu-disk.png'
import tireNoDiskIcon from '../assets/images/icons/pneu-no-disk.png'
import tireOwnIcon from '../assets/images/icons/pneu-vlastni.png'
import tireBuyIcon from '../assets/images/icons/pneu-nakup.png'
import storageIcon from '../assets/images/icons/uschova.png'

moment.locale('cs')
const localizer = momentLocalizer(moment)
const DnDCalendar = withDragAndDrop(Calendar)
const CALENDAR_DAY_COUNT_STORAGE_KEY = 'reservationsVisibleDayCount'
const getDefaultVisibleDayCount = () => {
  if (typeof window === 'undefined') return 4
  if (window.innerWidth > 1900) return 5
  if (window.innerWidth >= 1600) return 4
  if (window.innerWidth >= 1200) return 3
  if (window.innerWidth >= 768) return 2
  return 1
}
const getInitialVisibleDayCount = () => {
  if (typeof window === 'undefined') return getDefaultVisibleDayCount()
  try {
    const savedValue = Number(window.sessionStorage.getItem(CALENDAR_DAY_COUNT_STORAGE_KEY))
    if (Number.isInteger(savedValue) && savedValue >= 1 && savedValue <= 5) return savedValue
  } catch {
    // Použijeme výchozí hodnotu podle šířky zařízení.
  }
  return getDefaultVisibleDayCount()
}
const CALENDAR_FORMATS = {
  timeGutterFormat: (date) => ([0, 30].includes(moment(date).minutes()) ? moment(date).format('H:mm') : '')
}
const DEFAULT_CALENDAR_SETTINGS = {
  workStart: '06:00',
  workEnd: '16:00',
  closedHolidays: true,
  closedSaturday: true,
  closedSunday: true,
  slotIntervalMinutes: 30,
}
const STANI_COUNT = 3
const STANI_RESOURCES = [
  { id: 1, title: 'osobní 1' },
  { id: 2, title: 'osobní 2' },
  { id: 3, title: 'truck' }
]
const RES_STATUS = {
  1: { label: 'přijato', color: 'success' },
  2: { label: 'na dílně', color: 'warning' },
  3: { label: 'uzavřeno', color: 'danger' }
}
const VEHICLE_TYPES = {
  1: 'osobní',
  2: 'suv',
  3: 'dodávka',
  4: 'truck',
  5: 'moto'
}
const STORAGE_SEASON = {
  1: 'letní',
  2: 'zimní',
  3: 'celoroční'
}
const STORAGE_DISK = {
  0: 'bez disku',
  1: 'ocelový disk',
  2: 'hliníkový disk'
}

const getEasterSunday = (year) => {
  const a = year % 19
  const b = Math.floor(year / 100)
  const c = year % 100
  const d = Math.floor(b / 4)
  const e = b % 4
  const g = Math.floor((8 * b + 13) / 25)
  const h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4)
  const k = c % 4
  const l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const month = Math.floor((h + l - 7 * m + 114) / 31) // 3=March, 4=April
  const day = ((h + l - 7 * m + 114) % 31) + 1
  return new Date(year, month - 1, day)
}

const isCzechHoliday = (date) => {
  const year = date.getFullYear()
  const fixed = new Set([
    `${year}-01-01`,
    `${year}-05-01`,
    `${year}-05-08`,
    `${year}-07-05`,
    `${year}-07-06`,
    `${year}-09-28`,
    `${year}-10-28`,
    `${year}-11-17`,
    `${year}-12-24`,
    `${year}-12-25`,
    `${year}-12-26`
  ])
  const easterSunday = getEasterSunday(year)
  const goodFriday = moment(easterSunday).subtract(2, 'days')
  const easterMonday = moment(easterSunday).add(1, 'day')
  fixed.add(goodFriday.format('YYYY-MM-DD'))
  fixed.add(easterMonday.format('YYYY-MM-DD'))
  return fixed.has(moment(date).format('YYYY-MM-DD'))
}

const extractCustomerNote = (value) => {
  const text = String(value || '').trim()
  if (!text) return ''
  // Backward compatibility for older public reservations where note was composed
  // like: "vozidlo ...\núkon ...\npoznámka: ...".
  const match = text.match(/(?:^|\n)\s*poznámka:\s*(.+)$/i)
  return (match?.[1] || text).trim()
}
const MESSAGE_STATUS = {
  0: { variant: 'primary', label: 'neodesláno' },
  1: { variant: 'success', label: 'odesláno' },
  2: { variant: 'danger', label: 'nedoručeno' }
}
const VEHICLE_TYPE_ICONS = {
  1: carOsIcon,
  2: carSuvIcon,
  3: carVanIcon,
  4: carTruckIcon,
  5: carBikeIcon
}
const SERVICE_ICONS = {
  2: defectIcon,
  3: balanceIcon
}
const TIRE_OPTION_ICONS = {
  1: tireDiskIcon,
  2: tireNoDiskIcon,
  3: tireOwnIcon,
  4: tireBuyIcon
}

const toClockMinutes = (value, fallback) => {
  const match = String(value || '').trim().match(/^(\d{2}):(\d{2})$/)
  if (!match) return fallback
  const h = Number(match[1])
  const m = Number(match[2])
  if (!Number.isFinite(h) || !Number.isFinite(m)) return fallback
  if (h < 0 || h > 23 || m < 0 || m > 59) return fallback
  return h * 60 + m
}

const toClockString = (minutes) => {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

const buildTimeOptions = (settings) => {
  const start = toClockMinutes(settings?.workStart, 6 * 60)
  const end = toClockMinutes(settings?.workEnd, 16 * 60)
  const step = Number(settings?.slotIntervalMinutes || 30)
  if (!Number.isFinite(start) || !Number.isFinite(end) || !Number.isFinite(step) || end <= start || step <= 0) {
    return []
  }
  const options = []
  for (let current = start; current <= end; current += step) {
    options.push(toClockString(current))
  }
  return options
}


const isWorkday = (date) => {
  const day = date.getDay()
  return day !== 0 && day !== 6
}

const normalizeToWorkday = (date, localizer) => {
  let cursor = localizer.startOf(date, 'day')
  while (!isWorkday(cursor)) {
    cursor = localizer.add(cursor, 1, 'day')
  }
  return cursor
}

const moveByWorkdays = (date, days, localizer) => {
  if (!days) return localizer.startOf(date, 'day')
  const direction = days > 0 ? 1 : -1
  let cursor = localizer.startOf(date, 'day')
  let left = Math.abs(days)
  while (left > 0) {
    cursor = localizer.add(cursor, direction, 'day')
    if (isWorkday(cursor)) left -= 1
  }
  return cursor
}

const tabletThreeDayRange = (date, options) => {
  const { localizer } = options
  const range = []
  let cursor = normalizeToWorkday(date, localizer)
  while (range.length < 3) {
    if (isWorkday(cursor)) {
      range.push(cursor)
    }
    cursor = localizer.add(cursor, 1, 'day')
  }
  return range
}

const TabletThreeDayWorkWeekView = ({
  date,
  localizer,
  min = localizer.startOf(new Date(), 'day'),
  max = localizer.endOf(new Date(), 'day'),
  scrollToTime = localizer.startOf(new Date(), 'day'),
  enableAutoScroll = true,
  ...props
}) => (
  <TimeGrid
    {...props}
    range={tabletThreeDayRange(date, { localizer })}
    eventOffset={15}
    localizer={localizer}
    min={min}
    max={max}
    scrollToTime={scrollToTime}
    enableAutoScroll={enableAutoScroll}
  />
)

TabletThreeDayWorkWeekView.defaultProps = TimeGrid.defaultProps
TabletThreeDayWorkWeekView.range = tabletThreeDayRange
TabletThreeDayWorkWeekView.navigate = (date, action, { localizer }) => {
  switch (action) {
    case 'PREV':
      return moveByWorkdays(date, -1, localizer)
    case 'NEXT':
      return moveByWorkdays(date, 1, localizer)
    case 'TODAY':
      return new Date()
    default:
      return date
  }
}
TabletThreeDayWorkWeekView.title = (date, { localizer }) => {
  const range = tabletThreeDayRange(date, { localizer })
  return localizer.format({ start: range[0], end: range[range.length - 1] }, 'dayRangeHeaderFormat')
}

const createAdjustableWorkdayView = (dayCount) => {
  const range = (date, { localizer }) => {
    const days = []
    let cursor = normalizeToWorkday(date, localizer)
    while (days.length < dayCount) {
      if (isWorkday(cursor)) days.push(cursor)
      cursor = localizer.add(cursor, 1, 'day')
    }
    return days
  }
  const AdjustableWorkdayView = ({
    date,
    localizer,
    min = localizer.startOf(new Date(), 'day'),
    max = localizer.endOf(new Date(), 'day'),
    scrollToTime = localizer.startOf(new Date(), 'day'),
    enableAutoScroll = true,
    ...props
  }) => (
    <TimeGrid
      {...props}
      range={range(date, { localizer })}
      eventOffset={15}
      localizer={localizer}
      min={min}
      max={max}
      scrollToTime={scrollToTime}
      enableAutoScroll={enableAutoScroll}
    />
  )
  AdjustableWorkdayView.displayName = `AdjustableWorkdayView${dayCount}`
  AdjustableWorkdayView.defaultProps = TimeGrid.defaultProps
  AdjustableWorkdayView.range = range
  AdjustableWorkdayView.navigate = TabletThreeDayWorkWeekView.navigate
  AdjustableWorkdayView.title = (date, { localizer }) => {
    const days = range(date, { localizer })
    return localizer.format({ start: days[0], end: days[days.length - 1] }, 'dayRangeHeaderFormat')
  }
  return AdjustableWorkdayView
}

const SLOT_TITLE_MODES = {
  vehicle: 'SPZ, vozidlo, jméno a poznámka',
  customer: 'Jméno a firma',
  contact: 'Telefon a e-mail'
}

const ReservationCalendarToolbar = ({
  label, onNavigate, onSearch, visibleDayCount, onDecreaseDays, onIncreaseDays,
  slotTitleMode, onCycleSlotTitleMode, onOpenOccupancy
}) => {
  return (
    <div className='rbc-toolbar'>
      <span className='rbc-btn-group'>
        <button type='button' onClick={() => onNavigate('TODAY')}>Dnes</button>
        <button type='button' onClick={() => onNavigate('PREV')}>Předchozí</button>
        <button type='button' onClick={() => onNavigate('NEXT')}>Další</button>
      </span>
      <span className='rbc-toolbar-label'>{label}</span>
      <span className='rbc-btn-group reservation-calendar-tools'>
        <button type='button' disabled={visibleDayCount <= 1} title='Zmenšit počet zobrazených dnů' aria-label='Zmenšit počet zobrazených dnů' onClick={onDecreaseDays}>
          <i className='bi bi-dash-lg'></i>
        </button>
        <button type='button' disabled={visibleDayCount >= 5} title='Zvětšit počet zobrazených dnů' aria-label='Zvětšit počet zobrazených dnů' onClick={onIncreaseDays}>
          <i className='bi bi-plus-lg'></i>
        </button>
        <button type='button' title={`Změnit titulky slotů: ${SLOT_TITLE_MODES[slotTitleMode]}`} aria-label='Změnit titulky slotů' onClick={onCycleSlotTitleMode}>
          <i className='bi bi-eye'></i>
        </button>
        <button type='button' title='Přehled obsazenosti na 5 týdnů' aria-label='Přehled obsazenosti' onClick={onOpenOccupancy}>
          <i className='bi bi-table'></i>
        </button>
        <button type='button' title='Hledat, filtrovat a exportovat rezervace' aria-label='Hledat rezervace' onClick={onSearch}>
          <i className='bi bi-search'></i>
        </button>
      </span>
    </div>
  )
}

const Reservations = () => {
  const navigate = useNavigate()
  const [view, setView] = useState(() => {
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      return window.matchMedia('(max-width: 767px)').matches ? Views.DAY : Views.WORK_WEEK
    }
    return Views.WORK_WEEK
  })
  const [date, setDate] = useState(new Date())
  const [visibleDayCount, setVisibleDayCount] = useState(getInitialVisibleDayCount)
  const [slotTitleMode, setSlotTitleMode] = useState('vehicle')
  const [isMobileCalendar, setIsMobileCalendar] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
    return window.matchMedia('(max-width: 767px)').matches
  })

  const [events, setEvents] = useState([])
  
  const [selectedSlot, setSelectedSlot] = useState(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const [selectedStani, setSelectedStani] = useState(1)
  const [addStav, setAddStav] = useState(1)
  const [addService, setAddService] = useState(1)
  const [addVariant, setAddVariant] = useState(1)
  const [addVehicleName, setAddVehicleName] = useState('')
  const [vehicleNameHints, setVehicleNameHints] = useState([])

  const [selectedEvent, setSelectedEvent] = useState(null)
  const [showEditModal, setShowEditModal] = useState(false)
  const [isCopyMode, setIsCopyMode] = useState(false)
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deletePending, setDeletePending] = useState(false)
  const [showOccupancyModal, setShowOccupancyModal] = useState(false)
  const [occupancyEvents, setOccupancyEvents] = useState([])
  const [occupancyLoading, setOccupancyLoading] = useState(false)
  const [occupancyError, setOccupancyError] = useState('')
  const [occupancySelection, setOccupancySelection] = useState(null)
  const [previewEvent, setPreviewEvent] = useState(null)
  const [previewSending, setPreviewSending] = useState('')
  const [formData, setFormData] = useState(null)
  const [isDateError, setIsDateError] = useState(false)
  const [hoverPreview, setHoverPreview] = useState(null)
  const calendarWrapRef = useRef(null)
  const calendarPointerStartRef = useRef(null)
  const calendarTouchDragRef = useRef(null)
  const addFormRef = useRef(null)
  
  const [validated, setValidated] = useState(false)
  const [notifications, setNotifications] = useState([])
  const [addUschovaInfo, setAddUschovaInfo] = useState(null)
  const [editUschovaInfo, setEditUschovaInfo] = useState(null)
  const [addSpzHints, setAddSpzHints] = useState([])
  const [editSpzHints, setEditSpzHints] = useState([])
  const [addSpzValue, setAddSpzValue] = useState('')
  const [showAddSpzHints, setShowAddSpzHints] = useState(false)
  const [showEditSpzHints, setShowEditSpzHints] = useState(false)
  const [calendarSettings, setCalendarSettings] = useState(DEFAULT_CALENDAR_SETTINGS)
  const [reservationLookups, setReservationLookups] = useState(null)
  const [reservationDetail, setReservationDetail] = useState(null)
  const [reservationHistory, setReservationHistory] = useState([])
  const [editDetailLoading, setEditDetailLoading] = useState(false)
  const [editDetailError, setEditDetailError] = useState('')
  const [addScheduleError, setAddScheduleError] = useState('')
  const [editScheduleError, setEditScheduleError] = useState('')
  const [editTab, setEditTab] = useState('karta')
  const [showSearchModal, setShowSearchModal] = useState(false)
  const [searchFilters, setSearchFilters] = useState({
    q: '', od: '', do: '', stav: '', typ: '', ukon: '', skupina: '', stani: '', ma_uschovu: '',
    radit: 'termin', smer: 'desc', limit: 50
  })
  const [searchResult, setSearchResult] = useState({ data: [], pagination: { strana: 1, limit: 50, celkem: 0, stran: 1 } })
  const [searchLoading, setSearchLoading] = useState(false)
  const [searchError, setSearchError] = useState('')

  const isDateBlocked = (value) => {
    const dateValue = value instanceof Date ? value : new Date(value)
    if (Number.isNaN(dateValue.getTime())) return false
    const day = dateValue.getDay()
    if (calendarSettings.closedSunday && day === 0) return true
    if (calendarSettings.closedSaturday && day === 6) return true
    if (calendarSettings.closedHolidays && isCzechHoliday(dateValue)) return true
    return false
  }

  const overlapsCalendarBreak = (startValue, endValue) => {
    const start = moment(startValue)
    const end = moment(endValue)
    if (!start.isValid() || !end.isValid()) return false
    return (calendarSettings.breakSlots || []).some((clock) => {
      const breakStartMinutes = toClockMinutes(clock, -1)
      if (breakStartMinutes < 0) return false
      const breakStart = start.clone().startOf('day').add(breakStartMinutes, 'minutes')
      const breakEnd = breakStart.clone().add(slotStepMinutes, 'minutes')
      return start.isBefore(breakEnd) && end.isAfter(breakStart)
    })
  }

  const timeOptions = useMemo(() => buildTimeOptions(calendarSettings), [calendarSettings])
  const slotStepMinutes = Number(calendarSettings.slotIntervalMinutes || 30)
  const workStartMinutes = toClockMinutes(calendarSettings.workStart, 6 * 60)
  const workEndMinutes = toClockMinutes(calendarSettings.workEnd, 16 * 60)
  const minCalendarTime = moment().set({
    hour: Math.floor(workStartMinutes / 60),
    minute: workStartMinutes % 60,
    second: 0,
    millisecond: 0
  }).toDate()
  const maxCalendarTime = moment().set({
    hour: Math.floor(workEndMinutes / 60),
    minute: workEndMinutes % 60,
    second: 0,
    millisecond: 0
  }).toDate()

  useEffect(() => {
    const fetchCalendarSettings = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/rezervace/calendar/settings`, {
          credentials: 'include'
        })
        if (!response.ok) return
        const data = await response.json()
        setCalendarSettings((prev) => ({
          ...prev,
          ...data
        }))
      } catch {
        // fallback na default
      }
    }
    fetchCalendarSettings()
  }, [])

  useEffect(() => {
    fetchRezervaceCiselniky()
      .then(setReservationLookups)
      .catch((error) => console.error('Chyba při načítání číselníků rezervací:', error))
  }, [])

  useEffect(() => {
    if (!showEditModal || !selectedEvent?.id) return undefined
    let active = true
    setEditDetailLoading(true)
    setEditDetailError('')
    Promise.all([
      fetchRezervaceDetail(selectedEvent.id),
      fetchRezervaceHistorie(selectedEvent.id)
    ])
      .then(([detail, history]) => {
        if (!active) return
        const item = detail?.rezervace || {}
        const phone = splitPhone(item.telefon)
        setReservationDetail(detail)
        setReservationHistory(Array.isArray(history) ? history : [])
        setFormData((prev) => ({
          ...(prev || {}),
          jmeno: item.jmeno || '',
          firma: item.firma || '',
          email: item.email || '',
          predvolba: phone.predvolba,
          telefon: phone.telefon,
          spz: item.spz || '',
          typ: item.typ || 1,
          znacka: item.znacka || '',
          id_servisniho_ukonu: item.id_servisniho_ukonu || '',
          id_varianty_prezuti: item.id_varianty_prezuti || '',
          poznamka: item.poznamka || '',
          stk: item.stk ? moment(item.stk).format('YYYY-MM-DD') : '',
          tachometr: item.tachometr ?? '',
          id_skupiny: item.id_skupiny || 1,
          stani: Number(item.stani) || 1,
          stav: normalizeStav(item.stav),
          startDate: item.zacatek ? moment(item.zacatek).format('YYYY-MM-DD') : prev?.startDate || '',
          startTime: item.zacatek ? moment(item.zacatek).format('HH:mm') : prev?.startTime || '',
          endTime: item.konec ? moment(item.konec).format('HH:mm') : prev?.endTime || ''
        }))
      })
      .catch((error) => {
        if (active) setEditDetailError(error.message || 'Detail rezervace se nepodařilo načíst.')
      })
      .finally(() => {
        if (active) setEditDetailLoading(false)
      })
    return () => { active = false }
  }, [showEditModal, selectedEvent?.id])

  useEffect(() => {
    //console.log('selectedSlot:', selectedSlot)
  }, [selectedSlot])

  useEffect(() => {
    //console.log('formData:', formData)
  }, [formData])

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined
    const mobileMedia = window.matchMedia('(max-width: 767px)')
    const handleMobileChange = (e) => setIsMobileCalendar(e.matches)
    setIsMobileCalendar(mobileMedia.matches)
    if (typeof mobileMedia.addEventListener === 'function') {
      mobileMedia.addEventListener('change', handleMobileChange)
    } else {
      mobileMedia.addListener(handleMobileChange)
    }
    return () => {
      if (typeof mobileMedia.removeEventListener === 'function') {
        mobileMedia.removeEventListener('change', handleMobileChange)
      } else {
        mobileMedia.removeListener(handleMobileChange)
      }
    }
  }, [])

  const adjustableWorkdayView = useMemo(() => createAdjustableWorkdayView(visibleDayCount), [visibleDayCount])
  const calendarViews = useMemo(() => {
    if (isMobileCalendar) return ['day']
    return {
      day: true,
      work_week: adjustableWorkdayView
    }
  }, [isMobileCalendar, adjustableWorkdayView])

  const effectiveView = isMobileCalendar ? Views.DAY : view

  useEffect(() => {
    fetchData()
    // fetchData používá aktuální datum a pohled z tohoto renderu.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, effectiveView, visibleDayCount])

  useEffect(() => {
    if (isMobileCalendar) {
      setView(Views.DAY)
    }
  }, [isMobileCalendar])

  useEffect(() => {
    try {
      window.sessionStorage.setItem(CALENDAR_DAY_COUNT_STORAGE_KEY, String(visibleDayCount))
    } catch {
      // Kalendář funguje i v prohlížeči se zakázaným úložištěm.
    }
  }, [visibleDayCount])

  useEffect(() => {
    const calendarNode = calendarWrapRef.current
    if (!calendarNode) return undefined

    const dispatchMouseEvent = (type, touch, target, buttons) => {
      target.dispatchEvent(new MouseEvent(type, {
        bubbles: true,
        cancelable: true,
        view: window,
        button: 0,
        buttons,
        clientX: touch.clientX,
        clientY: touch.clientY,
        screenX: touch.screenX,
        screenY: touch.screenY
      }))
    }
    const handleTouchStart = (event) => {
      if (event.touches.length !== 1) return
      const target = event.target instanceof Element ? event.target : null
      const eventNode = target?.closest('.rbc-event')
      if (!eventNode) return

      const touch = event.touches[0]
      calendarTouchDragRef.current = { target: eventNode, lastTouch: touch }
      event.preventDefault()
      event.stopPropagation()
      dispatchMouseEvent('mousedown', touch, target, 1)
    }
    const handleTouchMove = (event) => {
      const gesture = calendarTouchDragRef.current
      if (!gesture || event.touches.length !== 1) return
      const touch = event.touches[0]
      gesture.lastTouch = touch
      event.preventDefault()
      event.stopPropagation()
      dispatchMouseEvent('mousemove', touch, gesture.target, 1)
    }
    const finishTouchGesture = (event) => {
      const gesture = calendarTouchDragRef.current
      if (!gesture) return
      const touch = event.changedTouches?.[0] || gesture.lastTouch
      calendarTouchDragRef.current = null
      event.preventDefault()
      event.stopPropagation()
      dispatchMouseEvent('mouseup', touch, gesture.target, 0)
    }

    calendarNode.addEventListener('touchstart', handleTouchStart, { capture: true, passive: false })
    window.addEventListener('touchmove', handleTouchMove, { capture: true, passive: false })
    window.addEventListener('touchend', finishTouchGesture, { capture: true, passive: false })
    window.addEventListener('touchcancel', finishTouchGesture, { capture: true, passive: false })

    return () => {
      calendarNode.removeEventListener('touchstart', handleTouchStart, true)
      window.removeEventListener('touchmove', handleTouchMove, true)
      window.removeEventListener('touchend', finishTouchGesture, true)
      window.removeEventListener('touchcancel', finishTouchGesture, true)
      calendarTouchDragRef.current = null
    }
  }, [])

  const getEndTimeFromStart = (startTime) => {
    const parsed = moment(startTime, 'HH:mm', true)
    if (!parsed.isValid()) return ''
    return parsed.add(slotStepMinutes, 'minutes').format('HH:mm')
  }
  const clockRangeOverlapsBreak = (startTime, endTime) => {
    const start = moment(startTime, 'HH:mm', true)
    const end = moment(endTime, 'HH:mm', true)
    if (!start.isValid() || !end.isValid() || !end.isAfter(start)) return false
    return overlapsCalendarBreak(start, end)
  }
  const getInsertStartTimeOptions = () =>
    timeOptions.filter((time) => {
      const endTime = getEndTimeFromStart(time)
      return endTime && moment(endTime, 'HH:mm').isSameOrBefore(moment(calendarSettings.workEnd, 'HH:mm'))
    })
  const normalizeStav = (value) => {
    const n = Number(value)
    return [1, 2, 3].includes(n) ? n : 1
  }
  const getStatusMeta = (stav) => RES_STATUS[normalizeStav(stav)] || RES_STATUS[1]
  const formatDate = (value) => {
    if (!value) return ''
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return ''
    return d.toLocaleDateString('cs-CZ')
  }
  const formatHistoryValue = (field, value) => {
    if (value === undefined || value === null || value === '') return '–'
    const id = String(value)
    if (id.trim().startsWith('{')) {
      try {
        const parsed = JSON.parse(id)
        if (parsed.zacatek && parsed.konec) {
          const start = moment(parsed.zacatek)
          const end = moment(parsed.konec)
          const stand = STANI_RESOURCES.find((item) => String(item.id) === String(parsed.stani))?.title
          return [
            `${start.format('D. M. YYYY HH:mm')}–${end.format('HH:mm')}`,
            stand
          ].filter(Boolean).join(', ')
        }
      } catch {
        // Neznámý historický formát ponecháme jako text a dovolíme jeho zalomení.
      }
    }
    const lookupByField = {
      stav: [reservationLookups?.stavy, 'id_stavu'],
      typ: [reservationLookups?.typyVozidel, 'id_typu'],
      id_servisniho_ukonu: [reservationLookups?.servisniUkony, 'id_ukonu'],
      id_varianty_prezuti: [reservationLookups?.variantyPrezuti, 'id_varianty'],
      id_skupiny: [reservationLookups?.skupiny, 'id_skupiny']
    }
    const lookup = lookupByField[String(field || '')]
    if (lookup) {
      const [items, idKey] = lookup
      const match = (items || []).find((item) => String(item[idKey]) === id)
      if (match?.nazev) return match.nazev
    }
    if (String(field || '') === 'stani') {
      return STANI_RESOURCES.find((item) => String(item.id) === id)?.title || id
    }
    return id
  }
  const normalizeSpzLocal = (value) => String(value || '').replace(/\s+/g, '').toUpperCase().trim()
  const splitPhone = (value) => {
    const raw = String(value || '').trim()
    if (!raw) return { predvolba: '+420', telefon: '' }
    const clean = raw.replace(/\s+/g, '')
    const prefixes = ['+420', '+421', '+48', '+49', '+43']
    const prefix = prefixes.find((p) => clean.startsWith(p))
    if (prefix) {
      return {
        predvolba: prefix,
        telefon: clean.slice(prefix.length)
      }
    }
    return {
      predvolba: '+420',
      telefon: clean.replace(/^\+/, '')
    }
  }
  const loadSpzHintsFromStorage = async (spz) => {
    const safeSpz = normalizeSpzLocal(spz)
    if (!safeSpz || safeSpz.length < 7) return []
    const response = await guardedFetch(`${process.env.REACT_APP_API_URL}/rezervace/uschova/spz-napoveda?q=${encodeURIComponent(safeSpz)}`)
    const data = await response.json()
    if (!response.ok) throw new Error(data?.message || 'Chyba při načítání našeptávače SPZ.')
    return Array.isArray(data) ? data : []
  }
  const guardedFetch = async (url, options = {}) => {
    const response = await fetch(url, {
      credentials: 'include',
      ...options,
      headers: {
        ...(options.headers || {})
      }
    })
    if (shouldForceLogout(response.status, url)) {
      forceLogout()
    }
    return response
  }
  const occupancyDays = useMemo(() => {
    const firstDay = moment(date).startOf('isoWeek')
    return Array.from({ length: 25 }, (_, index) => {
      const week = Math.floor(index / 5)
      const weekday = index % 5
      return firstDay.clone().add(week, 'weeks').add(weekday, 'days')
    })
  }, [date])
  const occupancySlots = useMemo(() => {
    const start = toClockMinutes(calendarSettings.workStart, 6 * 60)
    const end = toClockMinutes(calendarSettings.workEnd, 16 * 60)
    const slots = []
    for (let minutes = start; minutes < end; minutes += 30) slots.push(toClockString(minutes))
    return slots
  }, [calendarSettings.workStart, calendarSettings.workEnd])

  useEffect(() => {
    if (!showOccupancyModal || !occupancyDays.length) return undefined
    let active = true
    const loadOccupancy = async () => {
      setOccupancyLoading(true)
      setOccupancyError('')
      setOccupancySelection(null)
      try {
        const query = new URLSearchParams({
          od: occupancyDays[0].format('YYYY-MM-DD'),
          do: occupancyDays[occupancyDays.length - 1].format('YYYY-MM-DD')
        })
        const response = await guardedFetch(`${process.env.REACT_APP_API_URL}/rezervace?${query.toString()}`)
        const data = await response.json()
        if (!response.ok) throw new Error(data?.message || 'Obsazenost se nepodařilo načíst.')
        if (active) setOccupancyEvents(Array.isArray(data) ? data : [])
      } catch (error) {
        if (active) setOccupancyError(error.message || 'Obsazenost se nepodařilo načíst.')
      } finally {
        if (active) setOccupancyLoading(false)
      }
    }
    loadOccupancy()
    return () => { active = false }
    // guardedFetch pouze doplňuje přihlášení a při renderu mění referenci.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOccupancyModal, occupancyDays])
  const findSpzHintMatch = (list, spz) => {
    const safeSpz = normalizeSpzLocal(spz)
    if (!safeSpz) return null
    return (list || []).find((item) => normalizeSpzLocal(item?.spz) === safeSpz) || null
  }
  const applyStorageHintToAddForm = (item) => {
    if (!item || !addFormRef.current) return
    const form = addFormRef.current
    const { predvolba, telefon } = splitPhone(item.telefon)
    if (form.elements.namedItem('jmeno')) form.elements.namedItem('jmeno').value = item.jmeno || ''
    if (form.elements.namedItem('firma')) form.elements.namedItem('firma').value = item.firma || ''
    if (form.elements.namedItem('email')) form.elements.namedItem('email').value = item.email || ''
    if (form.elements.namedItem('predvolba')) form.elements.namedItem('predvolba').value = predvolba
    if (form.elements.namedItem('telefon')) form.elements.namedItem('telefon').value = telefon || ''
    if (form.elements.namedItem('typ')) form.elements.namedItem('typ').value = item.typ ? String(item.typ) : '1'
    if (form.elements.namedItem('znacka')) form.elements.namedItem('znacka').value = item.znacka || ''
    if (form.elements.namedItem('stk')) form.elements.namedItem('stk').value = item.stk ? moment(item.stk).format('YYYY-MM-DD') : ''
    if (form.elements.namedItem('tachometr')) form.elements.namedItem('tachometr').value = item.tachometr ?? ''
    if (form.elements.namedItem('id_skupiny')) form.elements.namedItem('id_skupiny').value = item.id_skupiny || 1
    setAddVehicleName(item.znacka || '')
  }
  const applyStorageHintToEditForm = (item) => {
    if (!item) return
    const { predvolba, telefon } = splitPhone(item.telefon)
    setFormData((prev) => ({
      ...prev,
      jmeno: item.jmeno || '',
      email: item.email || '',
      predvolba,
      telefon: telefon || '',
      typ: item.typ ? Number(item.typ) : '',
      znacka: item.znacka || ''
    }))
  }
  const clearAddPrefillFields = () => {
    if (!addFormRef.current) return
    const form = addFormRef.current
    if (form.elements.namedItem('jmeno')) form.elements.namedItem('jmeno').value = ''
    if (form.elements.namedItem('firma')) form.elements.namedItem('firma').value = ''
    if (form.elements.namedItem('email')) form.elements.namedItem('email').value = ''
    if (form.elements.namedItem('predvolba')) form.elements.namedItem('predvolba').value = '+420'
    if (form.elements.namedItem('telefon')) form.elements.namedItem('telefon').value = ''
    if (form.elements.namedItem('typ')) form.elements.namedItem('typ').value = '1'
    if (form.elements.namedItem('znacka')) form.elements.namedItem('znacka').value = ''
    if (form.elements.namedItem('stk')) form.elements.namedItem('stk').value = ''
    if (form.elements.namedItem('tachometr')) form.elements.namedItem('tachometr').value = ''
    if (form.elements.namedItem('id_skupiny')) form.elements.namedItem('id_skupiny').value = '1'
    setAddVehicleName('')
  }
  const clearEditPrefillFields = () => {
    setFormData((prev) => ({
      ...(prev || {}),
      jmeno: '',
      email: '',
      predvolba: '+420',
      telefon: '',
      typ: '',
      znacka: ''
    }))
  }
  const handleAddSpzInput = async (value) => {
    const safeSpz = normalizeSpzLocal(value)
    if (safeSpz.length < 7) {
      setAddSpzHints([])
      setShowAddSpzHints(false)
      clearAddPrefillFields()
      setAddUschovaInfo(null)
      return
    }
    try {
      const hints = await loadSpzHintsFromStorage(safeSpz)
      setAddSpzHints(hints)
      setShowAddSpzHints(hints.length > 0)
    } catch (error) {
      console.error('Chyba při načítání SPZ našeptávače:', error)
      setAddSpzHints([])
      setShowAddSpzHints(false)
    }
  }
  const handleAddSpzPick = async (item) => {
    if (!item?.spz) return
    try {
      setAddSpzValue(item.spz)
      applyStorageHintToAddForm(item)
      setShowAddSpzHints(false)
      await handleAddSpzLookup(item.spz)
    } catch (error) {
      console.error('Chyba při výběru SPZ z datalistu:', error)
    }
  }
  const handleEditSpzInput = async (value) => {
    const safeSpz = normalizeSpzLocal(value)
    if (safeSpz.length < 7) {
      setEditSpzHints([])
      setShowEditSpzHints(false)
      clearEditPrefillFields()
      setEditUschovaInfo(null)
      return
    }
    try {
      const hints = await loadSpzHintsFromStorage(safeSpz)
      setEditSpzHints(hints)
      setShowEditSpzHints(hints.length > 0)
    } catch (error) {
      console.error('Chyba při načítání SPZ našeptávače:', error)
      setEditSpzHints([])
      setShowEditSpzHints(false)
    }
  }
  const handleEditSpzPick = async (item) => {
    if (!item?.spz) return
    try {
      setFormData((prev) => ({ ...prev, spz: item.spz }))
      applyStorageHintToEditForm(item)
      setShowEditSpzHints(false)
      await handleEditSpzLookup(item.spz)
    } catch (error) {
      console.error('Chyba při výběru SPZ z datalistu:', error)
    }
  }
  const loadActiveUschovaBySpz = async (spz) => {
    const safeSpz = normalizeSpzLocal(spz)
    if (!safeSpz || safeSpz.length < 5) return null
    const res = await guardedFetch(`${process.env.REACT_APP_API_URL}/rezervace/uschova/spz?spz=${encodeURIComponent(safeSpz)}`)
    const data = await res.json()
    if (!res.ok) throw new Error(data?.message || 'Chyba při načítání úschovy.')
    return data?.found ? data : null
  }

  // Načtení rezervací
  const fetchData = async () => {
    try {
      const rangeStart = moment(date).startOf('day')
      if (effectiveView !== Views.DAY) {
        while ([0, 6].includes(rangeStart.day())) rangeStart.add(1, 'day')
      }
      const rangeEnd = rangeStart.clone()
      if (effectiveView !== Views.DAY) {
        let remainingDays = visibleDayCount - 1
        while (remainingDays > 0) {
          rangeEnd.add(1, 'day')
          if (![0, 6].includes(rangeEnd.day())) remainingDays -= 1
        }
      }
      rangeEnd.endOf('day')
      const query = new URLSearchParams({
        od: rangeStart.format('YYYY-MM-DD'),
        do: rangeEnd.format('YYYY-MM-DD')
      })
      const response = await guardedFetch(`${process.env.REACT_APP_API_URL}/rezervace?${query.toString()}`)
      const list = await response.json()
      const mappedEvents = list.map(item => ({
        id: item.id_rezervace,
        title: `${item.jmeno}, ${item.spz}, ${item.telefon}`,
        start: new Date(item.zacatek),
        end: new Date(item.konec),
        resourceId: Number(item.stani) || 1,
        resource: {
          cas: `${new Date(item.zacatek).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} – ${new Date(item.konec).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`,
          jmeno: item.jmeno,
          firma: item.firma || '',
          email: item.email,
          spz: item.spz,          
          telefon: item.telefon,
          stav: normalizeStav(item.stav),
          typ: item.typ ? Number(item.typ) : null,
          znacka: item.znacka || '',
          id_servisniho_ukonu: item.id_servisniho_ukonu ? Number(item.id_servisniho_ukonu) : null,
          id_varianty_prezuti: item.id_varianty_prezuti ? Number(item.id_varianty_prezuti) : null,
          servisni_ukon_nazev: item.servisni_ukon_nazev || '',
          varianta_prezuti_nazev: item.varianta_prezuti_nazev || '',
          typ_vozidla_nazev: item.typ_vozidla_nazev || '',
          email_stav: Number(item.email_stav || 0),
          sms_stav: Number(item.sms_stav || 0),
          webuser: String(item.webuser || 'N').toUpperCase(),
          hasUschova: Number(item.ma_uschovu || 0) === 1,
          poznamka: extractCustomerNote(item.poznamka),
          stani: item.stani          
        }
      }))
      setEvents(mappedEvents)
      //console.log(mappedEvents)
    } catch (error) {
      console.error('Chyba při načítání dat:', error)
    } 
  }

  useAutoRefresh(fetchData, {
    shouldRun: () => !showAddModal && !showEditModal && !showPreviewModal && !showOccupancyModal,
    deps: [showAddModal, showEditModal, showPreviewModal, showOccupancyModal],
  })

  // Zavření modálu
  const closeModal = () => {
    setShowAddModal(false)
    setValidated(false)
    setSelectedStani(1)
    setAddStav(1)
    setAddService(1)
    setAddVariant(1)
    setAddVehicleName('')
    setVehicleNameHints([])
    setShowEditModal(false)
    setIsCopyMode(false)
    setShowPreviewModal(false)
    setPreviewEvent(null)
    setPreviewSending('')
    setSelectedEvent(null)
    setIsDateError(false)
    setAddUschovaInfo(null)
    setEditUschovaInfo(null)
    setAddSpzHints([])
    setEditSpzHints([])
    setAddSpzValue('')
    setShowAddSpzHints(false)
    setShowEditSpzHints(false)
    setAddScheduleError('')
    setEditScheduleError('')
    setReservationDetail(null)
    setReservationHistory([])
    setEditDetailError('')
    setEditDetailLoading(false)
    setEditTab('karta')
  }

  const buildReservationNote = (event, detail = null) => {
    if (!event) return ''
    const resource = event.resource || {}
    const service = [resource.servisni_ukon_nazev, resource.varianta_prezuti_nazev]
      .filter(Boolean)
      .join(' – ')
    const isTireChange = Number(resource.id_servisniho_ukonu) === 1
    const activeStorage = isTireChange
      ? (detail?.uschovy || []).find((item) => Number(item.stav) === 1)
      : null
    const serviceLine = service
      ? `servisní úkon: ${service}${activeStorage?.cislo ? ` (úschova: ${activeStorage.cislo})` : ''}`
      : (activeStorage?.cislo ? `číslo úschovy pneu: ${activeStorage.cislo}` : '')
    const lines = [
      `zákazník: ${resource.jmeno ?? ''}, ${resource.telefon ?? ''}, ${resource.email ?? ''}`,
      resource.znacka ? `značka a model: ${resource.znacka}` : '',
      serviceLine,
      resource.poznamka ? `poznámka rezervace: ${resource.poznamka}` : ''
    ]
    return lines.filter(Boolean).join('\n')
  }

  const handleCopyToOrder = async () => {
    if (!selectedEvent) return
    let detail = reservationDetail
    if (Number(detail?.rezervace?.id_rezervace) !== Number(selectedEvent.id)) {
      try {
        detail = await fetchRezervaceDetail(selectedEvent.id)
      } catch (error) {
        console.error('Nepodařilo se načíst úschovu pro přenos do objednávky:', error)
        detail = null
      }
    }
    const reservationNote = buildReservationNote(selectedEvent, detail)
    navigate('/ordercreate', {
      state: {
        reservationNote,
        reservationMeta: {
          id: selectedEvent.id,
          stani: selectedEvent.resource?.stani ?? '',
          spz: selectedEvent.resource?.spz ?? ''
        }
      }
    })
  }

  // Výběr časového slotu
  const handleSelectSlot = (slotInfo) => {
    setAddScheduleError('')
    const startMoment = moment(slotInfo.start)
    const endMoment = moment(slotInfo.end)
    const startTime = startMoment.isValid() ? startMoment.format('HH:mm') : ''
    const minEndMoment = startMoment.clone().add(slotStepMinutes, 'minutes')
    const effectiveEndMoment = endMoment.isValid() && endMoment.isAfter(minEndMoment)
      ? endMoment
      : minEndMoment
    const computedEndTime = effectiveEndMoment.isValid() ? effectiveEndMoment.format('HH:mm') : ''
    const nextStani = Number(slotInfo.resourceId || 1)
    setSelectedStani(nextStani)
    setSelectedSlot({
      startDate: startMoment.isValid() ? startMoment.format('YYYY-MM-DD') : '',
      startTime,
      endTime: computedEndTime
    })
    setAddStav(1)
    setAddService(1)
    setAddVariant(1)
    setAddVehicleName('')
    setVehicleNameHints([])
    setAddSpzValue('')
    setAddUschovaInfo(null)
    setShowAddModal(true)
  }

  const prepareEditEvent = (event) => {
    if (!event) return
    setEditScheduleError('')
    const phone = splitPhone(event.resource?.telefon)
    setSelectedEvent(event)
    setFormData({
      jmeno: event.resource?.jmeno || '',
      firma: event.resource?.firma || '',
      email: event.resource?.email || '',
      predvolba: phone.predvolba,
      telefon: phone.telefon,
      spz: event.resource?.spz || '',
      typ: event.resource?.typ || 1,
      znacka: event.resource?.znacka || '',
      id_servisniho_ukonu: event.resource?.id_servisniho_ukonu || '',
      id_varianty_prezuti: event.resource?.id_varianty_prezuti || '',
      stav: normalizeStav(event.resource?.stav),
      poznamka: event.resource?.poznamka || '',
      stani: Number(event.resource?.stani) || 1,
      startDate: event.start ? moment(event.start).format('YYYY-MM-DD') : '',
      startTime: event.start ? moment(event.start).format('HH:mm') : '',
      endTime: event.end ? moment(event.end).format('HH:mm') : ''
    })
    handleEditSpzLookup(event.resource?.spz || '')
  }

  const openPreviewEvent = (event) => {
    setHoverPreview(null)
    setPreviewEvent(event)
    setSelectedEvent(event)
    setShowPreviewModal(true)
  }

  const openPreviewEdit = () => {
    prepareEditEvent(previewEvent)
    setIsCopyMode(false)
    setShowPreviewModal(false)
    setShowEditModal(true)
  }

  const openPreviewCopy = () => {
    prepareEditEvent(previewEvent)
    setIsCopyMode(true)
    setShowPreviewModal(false)
    setShowEditModal(true)
  }

  const handlePreviewStatus = async (value) => {
    const nextStatus = Number(value)
    if (!previewEvent?.id || ![1, 2, 3].includes(nextStatus)) return
    try {
      await updateRezervaceStav(previewEvent.id, nextStatus)
      const updateEvent = (item) => item.id === previewEvent.id
        ? { ...item, resource: { ...item.resource, stav: nextStatus } }
        : item
      setEvents((prev) => prev.map(updateEvent))
      setPreviewEvent((prev) => ({ ...prev, resource: { ...prev.resource, stav: nextStatus } }))
    } catch (error) {
      setNotifications((prev) => [...prev, { id: Date.now(), type: 'danger', message: error.message, time: new Date().toLocaleTimeString() }])
    }
  }

  const handlePreviewMessage = async (type) => {
    if (!previewEvent?.id) return
    setPreviewSending(type)
    try {
      if (type === 'email') await sendRezervaceEmail(previewEvent.id)
      else await sendRezervaceSms(previewEvent.id)
      setPreviewEvent((prev) => ({ ...prev, resource: { ...prev.resource, [`${type}_stav`]: 1 } }))
      setEvents((prev) => prev.map((item) => item.id === previewEvent.id
        ? { ...item, resource: { ...item.resource, [`${type}_stav`]: 1 } }
        : item))
    } catch (error) {
      setPreviewEvent((prev) => ({ ...prev, resource: { ...prev.resource, [`${type}_stav`]: 2 } }))
      setEvents((prev) => prev.map((item) => item.id === previewEvent.id
        ? { ...item, resource: { ...item.resource, [`${type}_stav`]: 2 } }
        : item))
      setNotifications((prev) => [...prev, { id: Date.now(), type: 'danger', message: error.message, time: new Date().toLocaleTimeString() }])
    } finally {
      setPreviewSending('')
    }
  }

  // Handle pro změny v editačním formuláři
  const handleFormChange = e => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }
  const handleAddSpzLookup = async (spzValue) => {
    try {
      const info = await loadActiveUschovaBySpz(spzValue)
      setAddUschovaInfo(info)
    } catch (error) {
      console.error('Chyba při načítání úschovy:', error)
      setAddUschovaInfo(null)
    }
  }
  const handleEditSpzLookup = async (spzValue) => {
    try {
      const info = await loadActiveUschovaBySpz(spzValue)
      setEditUschovaInfo(info)
    } catch (error) {
      console.error('Chyba při načítání úschovy:', error)
      setEditUschovaInfo(null)
    }
  }

  // Přidat záznam
  const handleAddEvent = async (e) => {
    e.preventDefault()    
    const form = e.currentTarget
    const isValid = form.checkValidity()
    setValidated(true)
    if (!isValid) {
      return
    }
    const startDateTime = new Date(`${selectedSlot.startDate}T${selectedSlot.startTime}`)
    const endDateTime = new Date(`${selectedSlot.startDate}T${selectedSlot.endTime}`)
    const zacatek = moment(startDateTime).format('YYYY-MM-DD HH:mm:ss')
    const konec = moment(endDateTime).format('YYYY-MM-DD HH:mm:ss')
    const formData = new FormData(form)
    const jmeno = formData.get('jmeno')    
    const firma = String(formData.get('firma') || '').trim()
    const email = formData.get('email')
    const predvolba = formData.get('predvolba')
    const telefon = String(formData.get('telefon') || '').trim()
    const spz = formData.get('spz').replace(/\s+/g, '').toUpperCase()
    const typ = formData.get('typ') ? Number(formData.get('typ')) : null
    const znacka = String(formData.get('znacka') || '').trim()
    const id_servisniho_ukonu = Number(formData.get('id_servisniho_ukonu')) || null
    const id_varianty_prezuti = id_servisniho_ukonu === 1 ? Number(formData.get('id_varianty_prezuti')) || null : null
    const stk = formData.get('stk') || null
    const tachometr = formData.get('tachometr') === '' ? null : Number(formData.get('tachometr'))
    const id_skupiny = Number(formData.get('id_skupiny')) || 1
    const stav = normalizeStav(addStav)
    const poznamka = formData.get('poznamka')
    const stani = Number(formData.get('stani'))
    const celeCislo = telefon ? `${predvolba}${telefon}` : null
    const overlapsExistingReservation = events.some((event) => (
      Number(event.resourceId || event.resource?.stani || 1) === stani &&
      moment(event.start).isBefore(moment(endDateTime)) &&
      moment(event.end).isAfter(moment(startDateTime))
    ))
    if (overlapsExistingReservation) {
      const message = 'Vybraný termín je na tomto stání již obsazený.'
      setValidated(false)
      setAddScheduleError(message)
      setNotifications(prev => [...prev, {
        id: Date.now(),
        type: 'warning',
        message,
        time: new Date().toLocaleTimeString()
      }])
      return
    }
    const newItem = { jmeno, firma, email, telefon:celeCislo, spz, typ, znacka, id_servisniho_ukonu, id_varianty_prezuti, stk, tachometr, id_skupiny, stav, poznamka, stani, zacatek, konec }
    //console.log(newItem)
    let addResponseStatus = null
    try {
      const addResponse = await guardedFetch(`${process.env.REACT_APP_API_URL}/rezervace`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newItem),
      })
      addResponseStatus = addResponse.status
      if (!addResponse.ok) {
        const errorResponse = await addResponse.json()
        if (addResponse.status === 409) {
          setValidated(false)
          setAddScheduleError(errorResponse?.message || 'Vybraný termín je na tomto stání již obsazený.')
        }
        throw new Error(errorResponse.message)
      }
      const result = await addResponse.json()
      const newEvent = {
        id: result.id,
        title: `${jmeno}, ${spz}, ${telefon}`,
        start: new Date(zacatek),
        end: new Date(konec),
        resourceId: stani,
        resource: {
          jmeno: jmeno,
          firma,
          email: email || '',          
          spz: spz,
          telefon: celeCislo,
          typ,
          znacka,
          id_servisniho_ukonu,
          id_varianty_prezuti,
          stav,
          hasUschova: Boolean(addUschovaInfo?.found),
          poznamka: poznamka || '',          
          stani,          
        }
      }
      setEvents(prev => [...prev, newEvent])
      closeModal() 
      const newNotification = {
        id: Date.now(),
        type: 'success',
        message: `Nová rezervace byla uložena.`,
        time: new Date().toLocaleTimeString(),
      }
      setNotifications(prev => [...prev, newNotification])    
    } catch (error) {
      console.error('Chyba při ukládání záznamu:', error)
      const newNotification = {
        id: Date.now(),
        type: [400, 409].includes(addResponseStatus) ? 'warning' : 'danger',
        message: `Chyba při ukládání záznamu: ${error}`,
        time: new Date().toLocaleTimeString(),
      }
      setNotifications(prev => [...prev, newNotification])
    }
  }

  // Upravit záznam
  const handleEditEvent = async (e) => {
    e.preventDefault()
    const form = e.currentTarget
    const isValid = form.checkValidity()
    setValidated(true)
    if (!isValid) return
    const normalizedSpz = String(formData.spz || '').replace(/\s+/g, '').toUpperCase()
    const normalizedTelefon = String(formData.telefon || '').trim()
    const normalizedTyp = formData.typ ? Number(formData.typ) : null
    const normalizedZnacka = String(formData.znacka || '').trim()
    const normalizedStav = normalizeStav(formData.stav)
    const startDateTime = new Date(`${formData.startDate}T${formData.startTime}`)
    const endDateTime = new Date(`${formData.startDate}T${formData.endTime}`)
    const targetStand = Number(formData.stani) || 1
    const overlapsExistingReservation = events.some((event) => (
      (isCopyMode || String(event.id) !== String(selectedEvent.id)) &&
      Number(event.resourceId || event.resource?.stani || 1) === targetStand &&
      moment(event.start).isBefore(moment(endDateTime)) &&
      moment(event.end).isAfter(moment(startDateTime))
    ))
    if (overlapsExistingReservation) {
      const message = 'Vybraný termín je na tomto stání již obsazený.'
      setValidated(false)
      setEditScheduleError(message)
      setNotifications(prev => [...prev, {
        id: Date.now(),
        type: 'warning',
        message,
        time: new Date().toLocaleTimeString()
      }])
      return
    }
    const updatedEvent = {
      ...selectedEvent,
      start: startDateTime,
      end: endDateTime,
      title: `${formData.jmeno}, ${normalizedSpz}, ${normalizedTelefon}`,
      resourceId: targetStand,
      resource: {
        ...selectedEvent.resource,
        jmeno: formData.jmeno,
        email: formData.email,
        spz: normalizedSpz,
        telefon: normalizedTelefon ? `${formData.predvolba}${normalizedTelefon}` : null,
        typ: normalizedTyp,
        znacka: normalizedZnacka,
        firma: formData.firma || '',
        id_servisniho_ukonu: formData.id_servisniho_ukonu ? Number(formData.id_servisniho_ukonu) : null,
        id_varianty_prezuti: formData.id_varianty_prezuti ? Number(formData.id_varianty_prezuti) : null,
        stk: formData.stk || null,
        tachometr: formData.tachometr === '' ? null : Number(formData.tachometr),
        id_skupiny: Number(formData.id_skupiny) || 1,
        stav: normalizedStav,
        hasUschova: Boolean(editUschovaInfo?.found),
        poznamka: formData.poznamka,
        stani: formData.stani,
        zacatek: moment(startDateTime).format('YYYY-MM-DD HH:mm:ss'),
        konec: moment(endDateTime).format('YYYY-MM-DD HH:mm:ss')
      }
    }
    //console.log(updatedEvent)
    try {
      const response = await guardedFetch(
        isCopyMode
          ? `${process.env.REACT_APP_API_URL}/rezervace`
          : `${process.env.REACT_APP_API_URL}/rezervace/${selectedEvent.id}`,
        {
        method: isCopyMode ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedEvent.resource)
      })
      if (!response.ok) {
        const errorResponse = await response.json()
        if (response.status === 409) {
          setValidated(false)
          setEditScheduleError(errorResponse?.message || 'Vybraný termín je na tomto stání již obsazený.')
        }
        throw new Error(errorResponse?.message || 'Rezervaci se nepodařilo upravit.')
      }
      if (isCopyMode) await fetchData()
      else setEvents(prev => prev.map(e => (e.id === selectedEvent.id ? updatedEvent : e)))
      closeModal()
      setNotifications(prev => [
        ...prev,
        {
          id: Date.now(),
          type: 'success',
          message: isCopyMode ? 'Kopie rezervace byla vytvořena.' : 'Rezervace byla upravena.',
          time: new Date().toLocaleTimeString(),
        }
      ])
    } catch (error) {
      console.error('Chyba při ukládání záznamu:', error)
      setNotifications(prev => [
        ...prev,
        {
          id: Date.now(),
          type: 'danger',
          message: `Chyba při ukládání záznamu: ${error}`,
          time: new Date().toLocaleTimeString(),
        }
      ])
    }
  }

  // Přesunout záznam
  const handleMoveEvent = async ({ event, start, end, resourceId }) => {
    const isSameDay = moment(start).isSame(end, 'day')
    if (view === 'month' && (isDateBlocked(start) || isDateBlocked(end))) {
      setNotifications(prev => [
        ...prev,
        {
          id: Date.now(),
          type: 'warning',
          message: 'Rezervaci nelze přesunout na tento den.',
          time: new Date().toLocaleTimeString(),
        }
      ])
      return
    }
    // Zákaz roztažení události přes více dnů v měsíčním zobrazení
    if (view === 'month' && !isSameDay) {
      setNotifications(prev => [
        ...prev,
        {
          id: Date.now(),
          type: 'warning',
          message: 'Rezervaci nelze rozšířit přes více dnů.',
          time: new Date().toLocaleTimeString(),
        }
      ])
      return
    }
    // Povolené přesuny
    const nextStani = Number(resourceId || event.resourceId || event.resource?.stani || 1)
    const overlapsExistingReservation = events.some((existingEvent) => (
      String(existingEvent.id) !== String(event.id) &&
      Number(existingEvent.resourceId || existingEvent.resource?.stani || 1) === nextStani &&
      moment(existingEvent.start).isBefore(moment(end)) &&
      moment(existingEvent.end).isAfter(moment(start))
    ))
    if (overlapsExistingReservation) {
      setNotifications(prev => [...prev, {
        id: Date.now(),
        type: 'warning',
        message: 'Rezervaci nelze přesunout na obsazený termín stejného stání.',
        time: new Date().toLocaleTimeString()
      }])
      return
    }
    if (overlapsCalendarBreak(start, end)) {
      setNotifications(prev => [...prev, {
        id: Date.now(),
        type: 'warning',
        message: 'Rezervaci nelze přesunout přes nastavenou polední přestávku.',
        time: new Date().toLocaleTimeString()
      }])
      return
    }
    const updatedEvent = {
      ...event,
      start,
      end,
      resourceId: nextStani,
      resource: {
        ...event.resource,
        stani: nextStani
      }
    }
    setEvents(events.map(evt => (evt.id === event.id ? updatedEvent : evt)))
    const updatedResource = {
      zacatek: moment(start).format('YYYY-MM-DD HH:mm:ss'),
      konec: moment(end).format('YYYY-MM-DD HH:mm:ss'),
      stani: nextStani
    }
    let responseStatus = null
    try {
      const response = await guardedFetch(`${process.env.REACT_APP_API_URL}/rezervace/presun/${event.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedResource)
      })
      responseStatus = response.status
      if (!response.ok) {
        const errorResponse = await response.json()
        throw new Error(errorResponse.message)
      }
      const newNotification = {
        id: Date.now(),
        type: 'success',
        message: `Rezervace byla upravena.`,
        time: new Date().toLocaleTimeString(),
      }
      setNotifications(prev => [...prev, newNotification]) 
    } catch (error) {
      console.error('Chyba při ukládání záznamu:', error)
      setEvents(prev => prev.map(evt => (evt.id === event.id ? event : evt)))
      setNotifications(prev => [
        ...prev,
        {
          id: Date.now(),
          type: [400, 409].includes(responseStatus) ? 'warning' : 'danger',
          message: `Chyba při ukládání záznamu: ${error}`,
          time: new Date().toLocaleTimeString(),
        }
      ])
    }
  }

  // Odstranit záznam
  const handleDeleteEvent = async (eventId) => {
    try {
      const response = await guardedFetch(`${process.env.REACT_APP_API_URL}/rezervace/${eventId}`, {
        method: 'DELETE'
      })
      if (!response.ok) {
        const errorResponse = await response.json()
        throw new Error(errorResponse.message)
      }
      setEvents(prev => prev.filter(e => e.id !== eventId))
      closeModal()
      setNotifications(prev => [
        ...prev,
        {
          id: Date.now(),
          type: 'success',
          message: 'Rezervace byla odstraněna.',
          time: new Date().toLocaleTimeString()
        }
      ])
    } catch (error) {
      console.error('Chyba při mazání události:', error)
      setNotifications(prev => [
        ...prev,
        {
          id: Date.now(),
          type: 'danger',
          message: `Chyba při mazání události: ${error.message}`,
          time: new Date().toLocaleTimeString()
        }
      ])
    }
  }

  // Vlastní formátovací komponenta pro zobrazené události 
  const CustomEvent = ({ event, onDelete, view, onHoverStart, onHoverMove, onHoverEnd, titleMode }) => {
    const {
      jmeno, firma, email, telefon, spz, hasUschova, typ, id_servisniho_ukonu,
      id_varianty_prezuti, poznamka, typ_vozidla_nazev, znacka,
      servisni_ukon_nazev, varianta_prezuti_nazev
    } = event.resource
    const vehicleIcon = VEHICLE_TYPE_ICONS[Number(typ)]
    const operationIcon = Number(id_servisniho_ukonu) === 1
      ? TIRE_OPTION_ICONS[Number(id_varianty_prezuti)]
      : SERVICE_ICONS[Number(id_servisniho_ukonu)]
    const customerLine = [firma, jmeno].filter(Boolean).join(', ')
    const display = titleMode === 'customer'
      ? { title: jmeno || 'BEZ JMÉNA', lines: [firma] }
      : titleMode === 'contact'
        ? { title: telefon || 'BEZ TELEFONU', lines: [email] }
        : { title: spz || 'BEZ SPZ', lines: [znacka, customerLine, poznamka] }
    return (
      <div
        className='rbc-custom-event-wrapper'
        data-reservation-id={event.id}
        style={{ height: '100%', width: '100%' }}
        onMouseEnter={(e) => onHoverStart?.(event, e)}
        onMouseMove={(e) => onHoverMove?.(event, e)}
        onMouseLeave={() => onHoverEnd?.()}
      >
        <div className='rbc-custom-event-header'>
          <span className='rbc-custom-event-licenceplate'>{display.title}</span>
          <span className='rbc-custom-event-icons'>
            {vehicleIcon ? <img src={vehicleIcon} alt='' title={typ_vozidla_nazev || 'Typ vozidla'} /> : null}
            {operationIcon ? <img src={operationIcon} alt='' title={varianta_prezuti_nazev || servisni_ukon_nazev || 'Servisní úkon'} /> : null}
          </span>
        </div>
        {display.lines.filter(Boolean).map((line, index) => (
          <div key={`${event.id}-slot-line-${index}`} className={index === 0 ? 'rbc-custom-event-vehicle' : 'rbc-custom-event-note'}>{line}</div>
        ))}
        {hasUschova ? <img src={storageIcon} alt='' className='rbc-custom-event-storage' title='Navázaná úschova' /> : null}
      </div>
    )
  }

  const CustomAgendaEvent = ({ event }) => {
    const { jmeno, telefon, poznamka } = event.resource
    const contactParts = [telefon, poznamka].filter(Boolean)
    return (
      <span>
        <strong>{jmeno}</strong>
        {contactParts.length > 0 ? `, ${contactParts.join(' • ')}` : ''}
      </span>
    )
  }

  const buildHoverPreview = (event) => {
    if (!event) return null
    const eventDate = event.start ? moment(event.start).format('D.M.YYYY') : ''
    const startTime = event.start ? moment(event.start).format('HH:mm') : ''
    const endTime = event.end ? moment(event.end).format('HH:mm') : ''
    const durationMinutes = event.start && event.end ? Math.max(0, moment(event.end).diff(moment(event.start), 'minutes')) : 0
    const line1 = [
      [eventDate, startTime && endTime ? `${startTime} - ${endTime}` : '', durationMinutes ? `(${durationMinutes} min)` : ''].filter(Boolean).join(' '),
      event.resource?.spz || '',
      event.resource?.typ_vozidla_nazev || VEHICLE_TYPES[Number(event.resource?.typ)] || ''
    ]
      .filter(Boolean)
      .join(', ')
    const operation = event.resource?.servisni_ukon_nazev || ''
    const option = Number(event.resource?.id_servisniho_ukonu) === 1
      ? event.resource?.varianta_prezuti_nazev || ''
      : ''
    const vehicleName = String(event.resource?.znacka || '').trim()
    const serviceName = [operation, option].filter(Boolean).join(' – ')
    const line2 = vehicleName || '-'
    const line3 = serviceName || '-'
    const line4 = [event.resource?.jmeno || '', event.resource?.firma || '', event.resource?.telefon || '', event.resource?.email || '']
      .filter(Boolean)
      .join(', ')
    const line5 = String(event.resource?.poznamka || '').trim() || '-'
    return { line1: line1 || '-', line2, line3, line4: line4 || '-', line5 }
  }

  const getHoverPreviewPosition = (mouseEvent) => {
    if (!mouseEvent || !calendarWrapRef.current) return null
    const wrapRect = calendarWrapRef.current.getBoundingClientRect()
    const tooltipWidth = 360
    const tooltipHeight = 108
    const margin = 10
    const offsetX = 14
    const offsetY = 14
    let left = mouseEvent.clientX - wrapRect.left + offsetX
    let top = mouseEvent.clientY - wrapRect.top + offsetY
    if (left + tooltipWidth > wrapRect.width - margin) {
      left = mouseEvent.clientX - wrapRect.left - tooltipWidth - offsetX
    }
    if (left < margin) left = margin
    if (top + tooltipHeight > wrapRect.height - margin) {
      top = mouseEvent.clientY - wrapRect.top - tooltipHeight - offsetY
    }
    if (top < margin) top = margin
    return { left, top }
  }

  const showHoverPreview = (event, mouseEvent) => {
    const base = buildHoverPreview(event)
    const pos = getHoverPreviewPosition(mouseEvent)
    if (!base || !pos) {
      setHoverPreview(base)
      return
    }
    setHoverPreview({ ...base, top: pos.top, left: pos.left })
  }
  const getEventPaletteByStatus = (stav) => {
    const normalized = normalizeStav(stav)
    if (normalized === 2) {
      return {
        backgroundColor: 'rgba(var(--bs-warning-rgb), 0.28)',
        webBackgroundColor: 'rgba(var(--bs-warning-rgb), 0.05)',
        borderColor: 'rgba(var(--bs-warning-rgb), 0.52)',
        stripeColor: 'rgba(var(--bs-warning-rgb), 0.1)'
      }
    }
    if (normalized === 3) {
      return {
        backgroundColor: 'rgba(var(--bs-danger-rgb), 0.22)',
        webBackgroundColor: 'rgba(var(--bs-danger-rgb), 0.05)',
        borderColor: 'rgba(var(--bs-danger-rgb), 0.45)',
        stripeColor: 'rgba(var(--bs-danger-rgb), 0.1)'
      }
    }
    return {
      backgroundColor: 'rgba(var(--bs-success-rgb), 0.22)',
      webBackgroundColor: 'rgba(var(--bs-success-rgb), 0.05)',
      borderColor: 'rgba(var(--bs-success-rgb), 0.45)',
      stripeColor: 'rgba(var(--bs-success-rgb), 0.1)'
    }
  }
  const renderUschovaSection = (info) => {
    if (!info?.found) return null
    const sum = info.souhrn || {}
    const sezonaLabel = STORAGE_SEASON[Number(sum.sezona)] || '-'
    const diskLabel = STORAGE_DISK[Number(sum.disk)] || '-'
    return (
      <div className='mt-4'>
        <h6 className='mb-2 text-uppercase text-muted'>Úschova</h6>
        <Row className='mb-2 small'>
          <Col xs={12}>
            <div className='d-flex flex-wrap align-items-center gap-4'>
              <span><strong>číslo zakázky:</strong> {info.zakazka?.cislo || '-'}</span>
              <span><strong>datum přijetí:</strong> {formatDate(info.zakazka?.datum_prijmu)}</span>
              <span><strong>sezóna:</strong> {sezonaLabel}</span>
              <span><strong>disk:</strong> {diskLabel}</span>
            </div>
          </Col>
        </Row>
        <Table size="sm" bordered responsive className='mb-0'>
          <thead>
            <tr>
              <th className='reservation-storage-head px-2 text-muted fw-normal small'>rozměr</th>
              <th className='reservation-storage-head px-2 text-muted fw-normal small'>značka</th>
              <th className='reservation-storage-head px-2 text-muted fw-normal small'>model</th>
              <th className='reservation-storage-head px-2 text-muted fw-normal small text-start'>počet ks</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{sum.rozmer || '-'}</td>
              <td>{sum.znacka || '-'}</td>
              <td>{sum.model || '-'}</td>
              <td className='text-start'>{sum.pocet || 0}</td>
            </tr>
          </tbody>
        </Table>
      </div>
    )
  }

  const loadSearchResults = async (page = 1, filters = searchFilters) => {
    setSearchLoading(true)
    setSearchError('')
    try {
      const result = await searchRezervace({ ...filters, strana: page })
      setSearchResult({
        data: Array.isArray(result?.data) ? result.data : [],
        pagination: result?.pagination || { strana: page, limit: Number(filters.limit) || 50, celkem: 0, stran: 1 }
      })
    } catch (error) {
      setSearchError(error.message || 'Rezervace se nepodařilo načíst.')
    } finally {
      setSearchLoading(false)
    }
  }

  const openSearchModal = () => {
    navigate('/reservations/search')
  }

  const openReservationFromSearch = (item) => {
    const phone = splitPhone(item.telefon)
    const event = {
      id: item.id_rezervace,
      start: new Date(item.zacatek),
      end: new Date(item.konec),
      resourceId: Number(item.stani) || 1,
      resource: item
    }
    setSelectedEvent(event)
    setFormData({
      jmeno: item.jmeno || '', email: item.email || '', predvolba: phone.predvolba,
      telefon: phone.telefon, spz: item.spz || '', typ: item.typ || 1,
      znacka: item.znacka || '', stav: normalizeStav(item.stav), poznamka: item.poznamka || '',
      stani: Number(item.stani) || 1, startDate: moment(item.zacatek).format('YYYY-MM-DD'),
      startTime: moment(item.zacatek).format('HH:mm'), endTime: moment(item.konec).format('HH:mm')
    })
    setShowSearchModal(false)
    setShowEditModal(true)
  }

  return ( 
    <> 
      <div
        ref={calendarWrapRef}
        className='position-relative mt-3 reservations-calendar-page'
        onPointerDownCapture={(e) => {
          const target = e.target
          const eventNode = target instanceof Element
            ? target.closest('[data-reservation-id]')
            : null

          if (!eventNode || target.closest('.rbc-addons-dnd-resize-ns-anchor')) {
            calendarPointerStartRef.current = null
            return
          }

          calendarPointerStartRef.current = {
            id: eventNode.getAttribute('data-reservation-id'),
            x: e.clientX,
            y: e.clientY,
            pointerId: e.pointerId
          }
        }}
        onPointerUpCapture={(e) => {
          const start = calendarPointerStartRef.current
          calendarPointerStartRef.current = null
          if (!start || start.pointerId !== e.pointerId) return

          const distance = Math.hypot(e.clientX - start.x, e.clientY - start.y)
          if (distance > 5) return

          const clickedEvent = events.find(event => String(event.id) === String(start.id))
          if (clickedEvent) {
            window.setTimeout(() => openPreviewEvent(clickedEvent), 0)
          }
        }}
        onMouseMove={(e) => {
          const target = e.target
          if (!(target instanceof Element)) return
          if (!target.closest('.rbc-event')) {
            setHoverPreview(null)
          }
        }}
        onMouseLeave={() => setHoverPreview(null)}
      >
      {hoverPreview && (
        <div
          className='small border rounded px-3 py-2 bg-body-tertiary shadow-sm position-absolute reservation-hover-preview'
          style={{
            top: hoverPreview.top,
            left: hoverPreview.left,
            zIndex: 30,
            pointerEvents: 'none',
            minWidth: 320,
            maxWidth: 420,
            opacity: 0.88
          }}
        >
          <div className='fw-bold'>{hoverPreview.line1}</div>
          <div className='text-muted reservation-hover-preview-detail'>{hoverPreview.line2}</div>
          <div className='text-muted reservation-hover-preview-detail'>{hoverPreview.line3}</div>
          <div className='text-muted reservation-hover-preview-detail'>{hoverPreview.line4}</div>
          <div className='text-muted reservation-hover-preview-detail'>{hoverPreview.line5}</div>
        </div>
      )}
      {/* Kalendář */}
      <div className={`reservations-calendar-scroll ${effectiveView === Views.WORK_WEEK ? 'is-work-week' : 'is-day-view'}`}>
        <div className='reservations-calendar-inner'>
          <DnDCalendar  
            localizer={localizer}
            culture="cs"
            formats={CALENDAR_FORMATS}
            events={events}
            resources={STANI_RESOURCES}
            resourceAccessor="resourceId"
            resourceIdAccessor="id"
            resourceTitleAccessor="title"
            resourceGroupingLayout
            titleAccessor={() => ''}
            tooltipAccessor={() => ''}
            eventPropGetter={(event) => {
              const palette = getEventPaletteByStatus(event?.resource?.stav)
              const isWebReservation = String(event?.resource?.webuser || 'N').toUpperCase() === 'Y'
              return {
                style: {
                  backgroundColor: isWebReservation ? palette.webBackgroundColor : palette.backgroundColor,
                  ...(isWebReservation ? {
                    backgroundImage: `repeating-linear-gradient(135deg, transparent 0, transparent 7px, ${palette.stripeColor} 7px, ${palette.stripeColor} 14px)`
                  } : {}),
                  borderColor: palette.borderColor,
                  color: 'var(--bs-emphasis-color)',
                  borderRadius: '4px'
                }
              }
            }}
            slotPropGetter={(slotDate) => {
              const slotEnd = moment(slotDate).add(slotStepMinutes, 'minutes')
              const isBreak = overlapsCalendarBreak(slotDate, slotEnd)
              const classes = moment(slotDate).hour() % 2 !== 0
                ? ['reservation-calendar-striped-hour']
                : []
              if (isBreak) classes.push('reservation-calendar-break')
              return {
                className: classes.join(' '),
                ...(isBreak ? { title: 'Polední přestávka' } : {})
              }
            }}
            components={{
              toolbar: (props) => (
                <ReservationCalendarToolbar
                  {...props}
                  onSearch={openSearchModal}
                  visibleDayCount={isMobileCalendar ? 1 : visibleDayCount}
                  onDecreaseDays={() => {
                    if (isMobileCalendar) return
                    setVisibleDayCount((count) => Math.max(1, count - 1))
                    setView(Views.WORK_WEEK)
                  }}
                  onIncreaseDays={() => {
                    if (isMobileCalendar) return
                    setVisibleDayCount((count) => Math.min(5, count + 1))
                    setView(Views.WORK_WEEK)
                  }}
                  slotTitleMode={slotTitleMode}
                  onCycleSlotTitleMode={() => setSlotTitleMode((mode) => (
                    mode === 'vehicle' ? 'customer' : mode === 'customer' ? 'contact' : 'vehicle'
                  ))}
                  onOpenOccupancy={() => setShowOccupancyModal(true)}
                />
              ),
              event: (props) => (
                <CustomEvent
                  {...props}
                  view={effectiveView}
                  onDelete={handleDeleteEvent}
                  onHoverStart={(evt, mouseEvent) => showHoverPreview(evt, mouseEvent)}
                  onHoverMove={(evt, mouseEvent) => showHoverPreview(evt, mouseEvent)}
                  onHoverEnd={() => setHoverPreview(null)}
                  titleMode={slotTitleMode}
                />
              ),
              agenda: {
                event: (props) => <CustomAgendaEvent {...props} />
              },
              dateCellWrapper: ({ value, children }) => {
                const classes = []
                if (isDateBlocked(value)) {
                  classes.push('disabled-day')
                }
                if (moment(value).isSame(new Date(), 'day')) {
                  classes.push('today-highlight')
                }
                if (!classes.length) return children
                return cloneElement(Children.only(children), {
                  className: [children.props.className, classes.join(' ')].filter(Boolean).join(' ')
                })
              },
              dateHeader: ({ date: headerDate, label, drilldownView, onDrillDown }) => {
                const isDisabled = isDateBlocked(headerDate)
                const isToday = moment(headerDate).isSame(new Date(), 'day')
                const className = [
                  'rbc-button-link',
                  isDisabled ? 'disabled-day' : '',
                  isToday ? 'today-highlight' : ''
                ].filter(Boolean).join(' ')
                if (drilldownView && onDrillDown) {
                  return (
                    <button
                      type="button"
                      className={className}
                      onClick={(e) => onDrillDown(e)}
                    >
                      {label}
                    </button>
                  )
                }
                return <span className={className}>{label}</span>
              }
            }}
            date={date}
            view={effectiveView}
            onView={newView => {
              if (isMobileCalendar && newView !== Views.DAY) return
              setView(newView)
            }}
            onNavigate={newDate => setDate(newDate)}
            onDragStart={() => setHoverPreview(null)}
            views={calendarViews}
            defaultView={isMobileCalendar ? Views.DAY : Views.WORK_WEEK}
            step={slotStepMinutes}
            timeslots={1}
            selectable
            onSelectSlot={(slotInfo) => {
              const { start } = slotInfo
              if (isDateBlocked(start)) {
                const newNotification = {
                  id: Date.now(),
                  type: 'warning',
                  message: 'Na tento den nelze vytvořit novou rezervaci.',
                  time: new Date().toLocaleTimeString(),
                }
                setNotifications(prev => [...prev, newNotification])
                return 
              }
              if (overlapsCalendarBreak(slotInfo.start, slotInfo.end)) {
                setNotifications(prev => [...prev, {
                  id: Date.now(),
                  type: 'warning',
                  message: 'Vybraný termín zasahuje do nastavené polední přestávky.',
                  time: new Date().toLocaleTimeString()
                }])
                return
              }
              handleSelectSlot(slotInfo)
            }}
            onSelectEvent={openPreviewEvent}
            onEventDrop={(args) => {
              setHoverPreview(null)
              handleMoveEvent(args)
            }}
            resizable
            onEventResize={(args) => {
              setHoverPreview(null)
              handleMoveEvent(args)
            }}
            messages={{
              today: 'Dnes',
              previous: 'Předchozí',
              next: 'Další',
              work_week: 'Týden',
              week: 'Týden',
              day: 'Den',
              date: 'Datum',
              time: 'Čas',
              event: 'Událost',
              noEventsInRange: 'Žádné události v tomto rozsahu.',
              showMore: total => `+${total} více`
            }}   
            dayPropGetter={(date) => {
              const classes = []
              if (isDateBlocked(date)) {
                classes.push('disabled-day')
              }
              if (moment(date).isSame(new Date(), 'day')) {
                classes.push('today-highlight')
              }
              return classes.length ? { className: classes.join(' ') } : {}
            }}   
            style={{ height: '100%' }}
            min={minCalendarTime}
            max={maxCalendarTime}
          />
        </div>
      </div>
      </div>

      <Modal show={showPreviewModal && Boolean(previewEvent)} onHide={() => setShowPreviewModal(false)} size='sm' centered dialogClassName='reservation-preview-modal'>
        <Modal.Header closeButton className='reservation-preview-header border-0 pb-2'>
          <div className='d-flex flex-wrap align-items-center gap-2 pe-4'>
            <Modal.Title className='mb-0 reservation-preview-title'>Karta rezervace</Modal.Title>
            {previewEvent?.resource?.email ? (() => {
              const meta = MESSAGE_STATUS[Number(previewEvent.resource.email_stav || 0)] || MESSAGE_STATUS[0]
              return <Button variant='link' className={`reservation-message-action text-${meta.variant}`} disabled={previewSending === 'email'} title={`E-mail: ${meta.label}`} onClick={() => handlePreviewMessage('email')}><i className='bi bi-envelope-fill me-1'></i>Email</Button>
            })() : null}
            {previewEvent?.resource?.telefon ? (() => {
              const meta = MESSAGE_STATUS[Number(previewEvent.resource.sms_stav || 0)] || MESSAGE_STATUS[0]
              return <Button variant='link' className={`reservation-message-action text-${meta.variant}`} disabled={previewSending === 'sms'} title={`SMS: ${meta.label}`} onClick={() => handlePreviewMessage('sms')}><i className='bi bi-bell-fill me-1'></i>SMS</Button>
            })() : null}
            <ButtonGroup className='reservation-preview-status' aria-label='Stav rezervace'>
              {Object.entries(RES_STATUS).map(([id, item]) => (
                <ToggleButton
                  key={`preview-status-${id}`}
                  id={`preview-status-${id}`}
                  type='radio'
                  name='preview-status'
                  value={id}
                  variant='outline-primary'
                  checked={Number(previewEvent?.resource?.stav || 1) === Number(id)}
                  onChange={(e) => handlePreviewStatus(e.currentTarget.value)}
                >
                  {item.label}
                </ToggleButton>
              ))}
            </ButtonGroup>
          </div>
        </Modal.Header>
        <Modal.Body className='pt-2'>
          <div className='reservation-preview-details'>
            <div><i className='bi bi-clock'></i><span>{previewEvent?.start ? moment(previewEvent.start).format('D.M.YYYY HH:mm') : '–'}–{previewEvent?.end ? moment(previewEvent.end).format('HH:mm') : '–'} ({previewEvent?.start && previewEvent?.end ? moment(previewEvent.end).diff(moment(previewEvent.start), 'minutes') : 0} min)</span></div>
            <div><i className='bi bi-car-front-fill'></i><span>{[
              previewEvent?.resource?.spz,
              previewEvent?.resource?.typ_vozidla_nazev || VEHICLE_TYPES[Number(previewEvent?.resource?.typ)],
              previewEvent?.resource?.znacka
            ].filter(Boolean).join(', ') || '–'}</span></div>
            {(previewEvent?.resource?.servisni_ukon_nazev || previewEvent?.resource?.varianta_prezuti_nazev) ? <div><i className='bi bi-tools'></i><span>{[previewEvent.resource.servisni_ukon_nazev, previewEvent.resource.varianta_prezuti_nazev].filter(Boolean).join(' – ')}</span></div> : null}
            <div><i className='bi bi-person-fill'></i><span>{[previewEvent?.resource?.jmeno, previewEvent?.resource?.firma].filter(Boolean).join(', ') || '–'}</span></div>
            {(previewEvent?.resource?.telefon || previewEvent?.resource?.email) ? <div><i className='bi bi-person-lines-fill'></i><span>{[previewEvent.resource.telefon, previewEvent.resource.email].filter(Boolean).join(', ')}</span></div> : null}
            <div><i className='bi bi-chat-left'></i><span>{previewEvent?.resource?.poznamka || '–'}</span></div>
          </div>
          <div className='d-flex flex-wrap gap-1 mt-3'>
            <Button variant='success' title='Upravit údaje rezervace' onClick={openPreviewEdit}>Upravit</Button>
            <Button variant='warning' title='Přesunout rezervaci na jiný termín' onClick={openPreviewEdit}>Přesun</Button>
            <Button variant='primary' title='Vytvořit kopii rezervace' onClick={openPreviewCopy}>Kopie</Button>
            <Button variant='danger' title='Trvale smazat rezervaci' onClick={() => setShowDeleteConfirm(true)}>Smazat</Button>
            <Button variant='primary' title='Přenést rezervaci do objednávky' onClick={handleCopyToOrder}>Objednávka</Button>
          </div>
        </Modal.Body>
      </Modal>

      <Modal show={showDeleteConfirm && Boolean(previewEvent)} onHide={() => !deletePending && setShowDeleteConfirm(false)} size='sm' centered backdrop='static'>
        <Modal.Body className='p-3'>
          <div className='d-flex gap-3 align-items-start'>
            <div className='reservation-delete-confirm-icon flex-shrink-0'><i className='bi bi-trash3'></i></div>
            <div>
              <h6 className='mb-1'>Smazat rezervaci?</h6>
              <p className='text-muted small mb-1'>
                Rezervace {previewEvent?.resource?.spz ? <strong>{previewEvent.resource.spz}</strong> : null}
                {previewEvent?.start ? ` z ${moment(previewEvent.start).format('D. M. YYYY HH:mm')}` : ''} bude trvale odstraněna.
              </p>
              <p className='small text-danger mb-0'>Tuto akci nelze vrátit zpět.</p>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className='border-0 px-3 pt-0 pb-3 justify-content-end reservation-delete-confirm-actions'>
          <Button variant='outline-secondary' disabled={deletePending} onClick={() => setShowDeleteConfirm(false)}>Zrušit</Button>
          <Button variant='danger' disabled={deletePending} onClick={async () => {
            setDeletePending(true)
            await handleDeleteEvent(previewEvent.id)
            setDeletePending(false)
            setShowDeleteConfirm(false)
          }}>
            {deletePending ? <><Spinner animation='border' size='sm' className='me-2' />Mažu…</> : 'Smazat rezervaci'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showSearchModal} onHide={() => setShowSearchModal(false)} fullscreen='lg-down' size='xl' centered scrollable>
        <Modal.Header closeButton className='border-0 px-4 pt-4 pb-2'>
          <Modal.Title className='text-primary'>
            <i className='bi bi-search me-2'></i>Vyhledávání rezervací
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className='px-4'>
          <Form
            onSubmit={(e) => {
              e.preventDefault()
              loadSearchResults(1)
            }}
          >
            <Row className='g-2 align-items-end mb-3'>
              <Col lg={4} md={6}>
                <FloatingLabel label='SPZ, jméno, firma, telefon, e-mail nebo poznámka'>
                  <Form.Control
                    type='search'
                    value={searchFilters.q}
                    onChange={(e) => setSearchFilters((prev) => ({ ...prev, q: e.target.value }))}
                    placeholder='Hledat'
                  />
                </FloatingLabel>
              </Col>
              <Col lg={2} md={3}>
                <FloatingLabel label='od'>
                  <Form.Control type='date' value={searchFilters.od} onChange={(e) => setSearchFilters((prev) => ({ ...prev, od: e.target.value }))} />
                </FloatingLabel>
              </Col>
              <Col lg={2} md={3}>
                <FloatingLabel label='do'>
                  <Form.Control type='date' value={searchFilters.do} onChange={(e) => setSearchFilters((prev) => ({ ...prev, do: e.target.value }))} />
                </FloatingLabel>
              </Col>
              <Col lg={2} md={4}>
                <FloatingLabel label='stav'>
                  <Form.Select value={searchFilters.stav} onChange={(e) => setSearchFilters((prev) => ({ ...prev, stav: e.target.value }))}>
                    <option value=''>Všechny</option>
                    {(reservationLookups?.stavy || []).map((item) => <option key={item.id_stavu} value={item.id_stavu}>{item.nazev}</option>)}
                  </Form.Select>
                </FloatingLabel>
              </Col>
              <Col lg={2} md={4}>
                <FloatingLabel label='typ vozidla'>
                  <Form.Select value={searchFilters.typ} onChange={(e) => setSearchFilters((prev) => ({ ...prev, typ: e.target.value }))}>
                    <option value=''>Všechny</option>
                    {(reservationLookups?.typyVozidel || []).map((item) => <option key={item.id_typu} value={item.id_typu}>{item.nazev}</option>)}
                  </Form.Select>
                </FloatingLabel>
              </Col>
              <Col lg={3} md={4}>
                <FloatingLabel label='servisní úkon'>
                  <Form.Select value={searchFilters.ukon} onChange={(e) => setSearchFilters((prev) => ({ ...prev, ukon: e.target.value }))}>
                    <option value=''>Všechny</option>
                    {(reservationLookups?.servisniUkony || []).map((item) => <option key={item.id_ukonu} value={item.id_ukonu}>{item.nazev}</option>)}
                  </Form.Select>
                </FloatingLabel>
              </Col>
              <Col lg={2} md={4}>
                <FloatingLabel label='skupina'>
                  <Form.Select value={searchFilters.skupina} onChange={(e) => setSearchFilters((prev) => ({ ...prev, skupina: e.target.value }))}>
                    <option value=''>Všechny</option>
                    {(reservationLookups?.skupiny || []).map((item) => <option key={item.id_skupiny} value={item.id_skupiny}>{item.nazev}</option>)}
                  </Form.Select>
                </FloatingLabel>
              </Col>
              <Col lg={2} md={4}>
                <FloatingLabel label='stání'>
                  <Form.Select value={searchFilters.stani} onChange={(e) => setSearchFilters((prev) => ({ ...prev, stani: e.target.value }))}>
                    <option value=''>Všechna</option>
                    {STANI_RESOURCES.map((item) => <option key={item.id} value={item.id}>{item.title}</option>)}
                  </Form.Select>
                </FloatingLabel>
              </Col>
              <Col lg={2} md={4}>
                <FloatingLabel label='úschova'>
                  <Form.Select value={searchFilters.ma_uschovu} onChange={(e) => setSearchFilters((prev) => ({ ...prev, ma_uschovu: e.target.value }))}>
                    <option value=''>Nerozhoduje</option>
                    <option value='1'>S úschovou</option>
                    <option value='0'>Bez úschovy</option>
                  </Form.Select>
                </FloatingLabel>
              </Col>
              <Col lg={3} md={8} className='d-flex gap-2'>
                <Button type='submit' className='flex-grow-1' disabled={searchLoading}>
                  {searchLoading ? <Spinner animation='border' size='sm' className='me-2' /> : <i className='bi bi-search me-2'></i>}
                  Vyhledat
                </Button>
                <Button
                  type='button'
                  variant='outline-secondary'
                  title='Vymazat filtry'
                  onClick={() => {
                    const cleared = { q: '', od: '', do: '', stav: '', typ: '', ukon: '', skupina: '', stani: '', ma_uschovu: '', radit: 'termin', smer: 'desc', limit: 50 }
                    setSearchFilters(cleared)
                    loadSearchResults(1, cleared)
                  }}
                >
                  <i className='bi bi-x-lg'></i>
                </Button>
              </Col>
            </Row>
          </Form>

          {searchError ? <Alert variant='danger'>{searchError}</Alert> : null}
          <div className='d-flex justify-content-between align-items-center mb-2'>
            <span className='text-muted small'>Nalezeno: <strong>{searchResult.pagination.celkem}</strong></span>
            <span className='text-muted small'>Strana {searchResult.pagination.strana} z {searchResult.pagination.stran}</span>
          </div>
          <div className='table-responsive rounded border reservation-search-results'>
            <Table hover className='align-middle mb-0 reservation-detail-table'>
              <thead className='table-light'>
                <tr>
                  <th>Termín</th><th>SPZ</th><th>Zákazník</th><th>Kontakt</th><th>Vozidlo</th><th>Úkon</th><th>Stání</th><th>Stav</th><th></th>
                </tr>
              </thead>
              <tbody>
                {searchResult.data.map((item) => (
                  <tr key={item.id_rezervace} role='button' onDoubleClick={() => openReservationFromSearch(item)}>
                    <td className='text-nowrap'>{moment(item.zacatek).format('D. M. YYYY HH:mm')}–{moment(item.konec).format('HH:mm')}</td>
                    <td className='fw-semibold'>{item.spz || '–'} {Number(item.ma_uschovu) === 1 ? <i className='bi bi-safe text-primary ms-1' title='Má úschovu'></i> : null}</td>
                    <td>{item.jmeno || '–'}{item.firma ? <div className='small text-muted'>{item.firma}</div> : null}</td>
                    <td>{item.telefon || '–'}{item.email ? <div className='small text-muted'>{item.email}</div> : null}</td>
                    <td>{item.typ_vozidla_nazev || '–'}</td>
                    <td>{item.servisni_ukon_nazev || '–'}</td>
                    <td>{STANI_RESOURCES.find((stand) => stand.id === Number(item.stani))?.title || item.stani}</td>
                    <td><Badge bg={item.stav_barva || 'secondary'}>{item.stav_nazev || '–'}</Badge></td>
                    <td><Button size='sm' variant='outline-primary' onClick={() => openReservationFromSearch(item)} title='Otevřít rezervaci'><i className='bi bi-box-arrow-up-right'></i></Button></td>
                  </tr>
                ))}
                {!searchLoading && !searchResult.data.length ? <tr><td colSpan='9' className='text-center text-muted py-4'>Žádné rezervace neodpovídají zadaným filtrům.</td></tr> : null}
              </tbody>
            </Table>
          </div>
          <div className='d-flex justify-content-center gap-2 mt-3'>
            <Button variant='outline-secondary' disabled={searchLoading || searchResult.pagination.strana <= 1} onClick={() => loadSearchResults(searchResult.pagination.strana - 1)}><i className='bi bi-chevron-left me-1'></i>Předchozí</Button>
            <Button variant='outline-secondary' disabled={searchLoading || searchResult.pagination.strana >= searchResult.pagination.stran} onClick={() => loadSearchResults(searchResult.pagination.strana + 1)}>Další<i className='bi bi-chevron-right ms-1'></i></Button>
          </div>
        </Modal.Body>
        <Modal.Footer className='border-0 px-4'>
          <Button variant='secondary' onClick={() => setShowSearchModal(false)}>Zavřít</Button>
        </Modal.Footer>
      </Modal>

      {/* Modál pro vložení */}
      <Modal show={showOccupancyModal} onHide={() => setShowOccupancyModal(false)} fullscreen='xl-down' size='xl' centered scrollable dialogClassName='reservation-occupancy-modal'>
        <Modal.Header closeButton className='border-0 px-3 py-2'>
          <Modal.Title className='h6 mb-0'><i className='bi bi-table me-2'></i>Obsazenost na 5 týdnů</Modal.Title>
        </Modal.Header>
        <Modal.Body className='px-3 pt-1 pb-3'>
          {occupancySelection ? (
            <div className='reservation-occupancy-selection d-flex flex-wrap align-items-center gap-2 mb-2 p-2 rounded border bg-body-tertiary'>
              <strong className='me-auto'>
                {occupancySelection.standTitle} · {occupancySelection.start.format('D. M. YYYY HH:mm')}
              </strong>
              <Button size='sm' onClick={() => {
                const selection = occupancySelection
                setShowOccupancyModal(false)
                handleSelectSlot({
                  start: selection.start.toDate(),
                  end: selection.end.toDate(),
                  resourceId: selection.stand
                })
              }}>Vytvořit rezervaci</Button>
              <Button size='sm' variant='outline-primary' onClick={() => {
                setDate(occupancySelection.start.toDate())
                setVisibleDayCount(1)
                setView(Views.WORK_WEEK)
                setShowOccupancyModal(false)
              }}>Přejít na vybraný den</Button>
              <Button size='sm' variant='outline-secondary' title='Zrušit výběr' aria-label='Zrušit výběr' onClick={() => setOccupancySelection(null)}><i className='bi bi-x-lg'></i></Button>
            </div>
          ) : null}
          {occupancyError ? <Alert variant='danger'>{occupancyError}</Alert> : null}
          {occupancyLoading ? <div className='text-muted py-4 text-center'><Spinner animation='border' size='sm' className='me-2' />Načítám obsazenost…</div> : (
            <div className='reservation-occupancy-scroll'>
              <div className='reservation-occupancy-grid'>
                {occupancyDays.map((day) => {
                  const holiday = isCzechHoliday(day.toDate())
                  return (
                    <section key={day.format('YYYY-MM-DD')} className={`reservation-occupancy-day ${holiday ? 'is-disabled' : ''}`}>
                      <header>
                        <strong>{day.format('dd D. M. YYYY')}</strong>
                        {holiday ? <span>svátek</span> : null}
                      </header>
                      {STANI_RESOURCES.map((stand) => (
                        <div className='reservation-occupancy-stand' key={`${day.format('YYYY-MM-DD')}-${stand.id}`}>
                          <span className='reservation-occupancy-stand-label' title={stand.title}>{stand.id}</span>
                          <div className='reservation-occupancy-slots' style={{ gridTemplateColumns: `repeat(${occupancySlots.length}, minmax(7px, 1fr))` }}>
                            {occupancySlots.map((time) => {
                              const start = moment(`${day.format('YYYY-MM-DD')} ${time}`, 'YYYY-MM-DD HH:mm')
                              const end = start.clone().add(30, 'minutes')
                              const occupied = occupancyEvents.some((item) => (
                                Number(item.stani) === stand.id &&
                                moment(item.zacatek).isBefore(end) &&
                                moment(item.konec).isAfter(start)
                              ))
                              const calendarBreak = overlapsCalendarBreak(start, end)
                              const disabled = holiday || calendarBreak || start.isBefore(moment())
                              return (
                                <button
                                  type='button'
                                  key={`${stand.id}-${time}`}
                                  className={`reservation-occupancy-slot ${disabled ? 'is-disabled' : occupied ? 'is-occupied' : 'is-free'}`}
                                  disabled={disabled || occupied}
                                  title={`${stand.title}, ${day.format('D. M. YYYY')} ${time}–${end.format('HH:mm')}${holiday ? ' – svátek' : calendarBreak ? ' – polední přestávka' : occupied ? ' – obsazeno' : ' – volno'}`}
                                  onClick={() => setOccupancySelection({ start, end, stand: stand.id, standTitle: stand.title })}
                                />
                              )
                            })}
                          </div>
                        </div>
                      ))}
                    </section>
                  )
                })}
              </div>
            </div>
          )}
          <div className='d-flex gap-3 mt-2 small text-muted'>
            <span><i className='reservation-occupancy-legend is-free'></i>volné</span>
            <span><i className='reservation-occupancy-legend is-occupied'></i>obsazené</span>
            <span><i className='reservation-occupancy-legend is-disabled'></i>nedostupné / svátek</span>
          </div>
        </Modal.Body>
      </Modal>

      <Modal show={showAddModal} fullscreen="xl-down" onHide={() => closeModal()} keyboard={true} size="lg" aria-labelledby="contained-modal-title-vcenter" centered scrollable>
        <Form ref={addFormRef} noValidate validated={validated} onSubmit={handleAddEvent} className='overflow-auto reservation-detail-form' >
          <Modal.Body className='px-3 pt-3 pb-2'> 
            <Tabs defaultActiveKey='karta' className='reservation-detail-tabs mb-3'>
              <Tab eventKey='karta' title={<><i className='bi bi-card-checklist me-2'></i>Karta rezervace</>}>
            {addScheduleError ? <Alert variant='danger' className='py-2 mb-2'><i className='bi bi-exclamation-triangle me-2'></i>{addScheduleError}</Alert> : null}
            <Row className='g-1'>    
              <Col md={4}>
                <Form.Group controlId="formSlotDate">
                  <FloatingLabel label="datum">
                    <Form.Control
                      type="date"
                      name="slotDate"
                      className={isDateError ? 'is-invalid' : ''}
                      isInvalid={isDateError || Boolean(addScheduleError)}
                      value={selectedSlot?.startDate || ''}
                      onChange={(e) => {
                        setAddScheduleError('')
                        const selectedDate = new Date(e.target.value)
                        if (isDateBlocked(selectedDate)) {
                          setSelectedSlot(s => ({ ...s, startDate: '' }))
                          setIsDateError(true)
                          return
                        }
                        setIsDateError(false)
                        setSelectedSlot(s => ({ ...s, startDate: e.target.value }))
                      }}
                      required
                    />
                    <Form.Control.Feedback type="invalid">
                      {isDateError ? 'Nelze vybrat tento den.' : addScheduleError}
                    </Form.Control.Feedback>
                  </FloatingLabel>
                </Form.Group>
              </Col>                  
              <Col md={4}>
                <Form.Group controlId="formSlotStart">
                  <FloatingLabel label="čas začátku">
                    <Form.Select
                      name="slotStart"
                      value={selectedSlot?.startTime}
                      isInvalid={Boolean(addScheduleError)}
                      onChange={(e) => {
                        setAddScheduleError('')
                        const startTime = e.target.value
                        const endTime = getEndTimeFromStart(startTime)
                        setSelectedSlot(s => ({ ...s, startTime, endTime }))
                      }}
                      required
                    >
                      {getInsertStartTimeOptions().map((time) => (
                        <option
                          key={time}
                          value={time}
                          disabled={clockRangeOverlapsBreak(time, getEndTimeFromStart(time))}
                        >
                          {time}
                        </option>
                      ))}
                    </Form.Select>
                    {addScheduleError ? <Form.Control.Feedback type="invalid">Změňte termín nebo servisní stání.</Form.Control.Feedback> : null}
                  </FloatingLabel>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group controlId="formSlotEnd">
                  <FloatingLabel label="čas konce">
                    <Form.Select
                      name="slotEnd"
                      value={selectedSlot?.endTime}
                      isInvalid={Boolean(addScheduleError)}
                      onChange={(e) => {
                        setAddScheduleError('')
                        const endTime = e.target.value
                        if (selectedSlot.startTime && endTime <= selectedSlot.startTime) {
                          const options = timeOptions
                          const endIndex = options.indexOf(endTime)
                          const newStartTime = options[endIndex - 1] || selectedSlot.startTime
                          setSelectedSlot(s => ({ ...s, startTime: newStartTime, endTime }))
                          return
                        }
                        setSelectedSlot(s => ({ ...s, endTime }))
                      }}
                      required
                    >
                      {timeOptions.slice(1).map((time) => (
                        <option
                          key={time}
                          value={time}
                          disabled={clockRangeOverlapsBreak(selectedSlot?.startTime, time)}
                        >
                          {time}
                        </option>
                      ))}
                    </Form.Select>
                    {addScheduleError ? <Form.Control.Feedback type="invalid">Změňte termín nebo servisní stání.</Form.Control.Feedback> : null}
                  </FloatingLabel>
                </Form.Group>
              </Col>                        
              <Col md={4}>
                <Form.Group controlId="formSpz" className='position-relative'>
                  <FloatingLabel label="spz">
                    <Form.Control
                      type="text"
                      name="spz"
                      value={addSpzValue}
                      placeholder="SPZ"
                      maxLength={12}
                      pattern="^[A-Za-z0-9]{5,9}$"
                      title="Zadejte SPZ bez mezer (5–9 znaků, písmena a čísla)"
                      onChange={(e) => {
                        const safeSpz = normalizeSpzLocal(e.target.value)
                        setAddSpzValue(safeSpz)
                        handleAddSpzInput(safeSpz)
                      }}
                      onFocus={(e) => setTimeout(() => e.target.select(), 0)}
                      onBlur={(e) => {
                        const safeSpz = normalizeSpzLocal(e.target.value)
                        setAddSpzValue(safeSpz)
                        setTimeout(() => setShowAddSpzHints(false), 120)
                        if (!safeSpz) {
                          clearAddPrefillFields()
                          setAddUschovaInfo(null)
                          setAddSpzHints([])
                          return
                        }
                        const selected = findSpzHintMatch(addSpzHints, safeSpz)
                        if (selected) {
                          applyStorageHintToAddForm(selected)
                          handleAddSpzLookup(selected.spz)
                        }
                      }}
                    />
                    <Form.Control.Feedback type="invalid">
                      Zadejte SPZ bez mezer (5–9 znaků, písmena a čísla)
                    </Form.Control.Feedback>
                  </FloatingLabel>
                  {showAddSpzHints && addSpzHints.length > 0 ? (
                    <div className='list-group position-absolute w-100 shadow-sm' style={{ zIndex: 20, top: '100%' }}>
                      {addSpzHints.map((item) => (
                        <button
                          key={`add-spz-hint-${item.spz}`}
                          type="button"
                          className='list-group-item list-group-item-action py-2'
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => handleAddSpzPick(item)}
                        >
                          <div className='fw-semibold'>{item.spz}</div>
                          <div className='small text-muted'>{item.jmeno || '-'}</div>
                        </button>
                      ))}
                    </div>
                  ) : null}
                </Form.Group>
              </Col>  
              <Col md={4}>
                <Form.Group controlId="formTyp">
                  <FloatingLabel label="typ vozidla">
                    <Form.Select name="typ" defaultValue="1">
                      {Object.entries(VEHICLE_TYPES).map(([key, label]) => (
                        <option key={key} value={key}>{label}</option>
                      ))}
                    </Form.Select>
                  </FloatingLabel>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group controlId="formZnacka">
                  <FloatingLabel label="značka a model">
                    <Form.Control
                      type="text"
                      name='znacka'
                      value={addVehicleName}
                      list='reservation-vehicle-name-hints'
                      onChange={async (e) => {
                        const value = e.target.value
                        setAddVehicleName(value)
                        if (value.trim().length < 2) {
                          setVehicleNameHints([])
                          return
                        }
                        try {
                          const response = await guardedFetch(`${process.env.REACT_APP_API_URL}/rezervace/napoveda/znacky?q=${encodeURIComponent(value.trim())}`)
                          const data = await response.json()
                          setVehicleNameHints(response.ok && Array.isArray(data) ? data : [])
                        } catch {
                          setVehicleNameHints([])
                        }
                      }}
                      placeholder="značka a model"
                      maxLength={255}
                    />
                    <datalist id='reservation-vehicle-name-hints'>
                      {vehicleNameHints.map((item) => <option key={item} value={item} />)}
                    </datalist>
                  </FloatingLabel>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId='formServiceAdd'>
                  <FloatingLabel label='servisní úkon'>
                    <Form.Select name='id_servisniho_ukonu' value={addService} onChange={(e) => {
                      const value = Number(e.target.value)
                      setAddService(value)
                      if (value !== 1) setAddVariant('')
                    }}>
                      {(reservationLookups?.servisniUkony || []).map((item) => <option key={item.id_ukonu} value={item.id_ukonu}>{item.nazev}</option>)}
                    </Form.Select>
                  </FloatingLabel>
                </Form.Group>
              </Col>
              {Number(addService) === 1 ? <Col md={6}>
                <Form.Group controlId='formServiceVariantAdd'>
                  <FloatingLabel label='varianta přezutí'>
                    <Form.Select name='id_varianty_prezuti' value={addVariant} onChange={(e) => setAddVariant(Number(e.target.value))} required>
                      {(reservationLookups?.variantyPrezuti || []).map((item) => <option key={item.id_varianty} value={item.id_varianty}>{item.nazev}</option>)}
                    </Form.Select>
                  </FloatingLabel>
                </Form.Group>
              </Col> : null}
              <Col xs={12}>
                <Form.Group controlId="formPoznamka">
                  <FloatingLabel label="poznámka">
                    <Form.Control
                      as="textarea"
                      name="poznamka"
                      style={{ height: '62px', minHeight: '62px', resize: 'none' }}
                      maxLength={256}
                    />
                  </FloatingLabel>
                </Form.Group>
              </Col>  
              <Col md={4} className='reservation-contact-row'>
                <Form.Group controlId="formJmeno">
                  <FloatingLabel label="jméno">
                    <Form.Control
                      type="text"
                      name='jmeno'
                      placeholder="Jméno"
                      maxLength={64}
                    />
                  </FloatingLabel>
                </Form.Group>
              </Col>
              <Col md={4} className='reservation-contact-row'>
                <Form.Group controlId='formFirmaAdd'>
                  <FloatingLabel label='firma'>
                    <Form.Control type='text' name='firma' placeholder='Firma' maxLength={150} />
                  </FloatingLabel>
                </Form.Group>
              </Col>
              <Col md={4} className='reservation-contact-row'>
                <Form.Group controlId="formTelefon">                    
                  <InputGroup>
                    <FormSelect name="predvolba" style={{ maxWidth: '100px' }}>
                      <option value="+420">+420</option>
                      <option value="+421">+421</option>
                      <option value="+48">+48</option>
                      <option value="+49">+49</option>
                      <option value="+43">+43</option>
                    </FormSelect>
                    <FloatingLabel label="telefon">
                      <FormControl
                        type="tel"
                        name='telefon'
                        placeholder="123456789"
                        pattern="^\d{9}$"
                        title="Zadejte 9 číslic bez mezer"
                        onFocus={(e) => setTimeout(() => e.target.select(), 0)}
                        onBlur={(e) => {
                          e.target.value = e.target.value
                            .replace(/\s+/g, '')
                        }}
                      />                             
                    </FloatingLabel>                      
                  </InputGroup>                                        
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group controlId="formEmail">
                  <FloatingLabel label="email">
                    <Form.Control
                      type="email"
                      name='email'
                      placeholder="Email"
                      maxLength={64}
                    />
                  </FloatingLabel>
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group controlId='formStkAdd'>
                  <FloatingLabel label='platnost STK'>
                    <Form.Control type='date' name='stk' />
                  </FloatingLabel>
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group controlId='formTachometrAdd'>
                  <FloatingLabel label='tachometr (km)'>
                    <Form.Control type='number' min='0' step='1' name='tachometr' placeholder='Tachometr' />
                  </FloatingLabel>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group controlId='formGroupAdd'>
                  <FloatingLabel label='skupina zákazníka'>
                    <Form.Select name='id_skupiny' defaultValue='1'>
                      {(reservationLookups?.skupiny || []).map((item) => <option key={item.id_skupiny} value={item.id_skupiny}>{item.nazev}</option>)}
                    </Form.Select>
                  </FloatingLabel>
                </Form.Group>
              </Col>
              <Col xs={12}>
                <div className='d-flex flex-wrap align-items-center gap-3 mt-3'>
                <div className={`d-flex align-items-center gap-1 rounded ${addScheduleError ? 'border border-danger p-1' : ''}`}>
                <span className='text-muted small flex-shrink-0'>stání</span>
                <Form.Group controlId="formStaniAdd" className='text-muted'>
                  <ButtonGroup>
                    {Array.from({ length: STANI_COUNT }, (_, i) => (
                      <ToggleButton
                        key={`stani-${i + 1}`}
                        type="radio"
                        name="stani"
                        variant="outline-primary"
                        id={`add-stani-${i + 1}`}
                        value={i + 1}
                        checked={parseInt(selectedStani) === i + 1}
                        onChange={(e) => {
                          setAddScheduleError('')
                          setSelectedStani(Number(e.currentTarget.value))
                        }}
                        required
                      >
                        <>
                          <i className={`bi ${i + 1 === 3 ? 'bi-truck-front' : 'bi-car-front'} pe-2`}></i>
                          {i + 1}
                        </>
                      </ToggleButton>
                    ))}
                  </ButtonGroup>
                </Form.Group>
                </div>
                {addScheduleError ? <div className='invalid-feedback d-block'>{addScheduleError}</div> : null}
                </div>
              </Col>
            </Row>
              </Tab>
              <Tab eventKey='uschova' title={<><i className='bi bi-safe me-2'></i>Úschova</>}>
                {renderUschovaSection(addUschovaInfo)}
              </Tab>
            </Tabs>
           </Modal.Body>
           <Modal.Footer className='border-0 d-flex justify-content-start px-3 pb-3'>
              <div>
                <Button className='me-1' variant="secondary" onClick={() => closeModal()}><i className='bi bi-arrow-left me-2'></i>Zpět</Button>
                <Button variant="primary" type="submit"><i className='bi bi-save me-2'></i>Uložit</Button>
              </div>
            </Modal.Footer>
        </Form>
      </Modal>

      {/* Modál pro editaci */}
      {showEditModal && formData && (
        <Modal show={showEditModal} fullscreen="lg-down" onHide={() => closeModal()} keyboard={true} size="lg" centered scrollable dialogClassName="reservation-detail-modal">
          <Form noValidate validated={validated} onSubmit={handleEditEvent} className='overflow-auto reservation-detail-form'>
            <Modal.Body className='px-3 pt-3 pb-2'>
              {editDetailError ? <Alert variant='danger'>{editDetailError}</Alert> : null}
              {editDetailLoading ? (
                <div className='d-flex align-items-center gap-2 text-muted mb-3'>
                  <Spinner animation='border' size='sm' /> Načítám detail rezervace…
                </div>
              ) : null}
              <Tabs activeKey={editTab} onSelect={(key) => setEditTab(key || 'karta')} className='reservation-detail-tabs mb-3'>
                <Tab eventKey='karta' title={<><i className='bi bi-card-checklist me-2'></i>Karta rezervace</>}>
              {editScheduleError ? <Alert variant='danger' className='py-2 mb-2'><i className='bi bi-exclamation-triangle me-2'></i>{editScheduleError}</Alert> : null}
              <Row className='g-1'>
                <Col md={4}>
                  <Form.Group controlId="formDate">
                    <FloatingLabel label="datum">
                      <Form.Control
                        type="date"
                        name="startDate"
                        className={isDateError ? 'is-invalid' : ''}
                        isInvalid={isDateError || Boolean(editScheduleError)}
                        value={formData.startDate}
                        //min={new Date().toISOString().slice(0, 10)}
                        onChange={(e) => {
                          setEditScheduleError('')
                          const selectedDate = new Date(e.target.value)
                          if (isDateBlocked(selectedDate)) {
                            e.target.value = ''
                            handleFormChange({ target: { name: 'startDate', value: '' } })
                            setIsDateError(true)
                            return
                          }
                          setIsDateError(false)
                          handleFormChange(e)
                        }}
                        required
                      />
                      {(isDateError || editScheduleError) && (
                        <Form.Control.Feedback type="invalid">
                          {isDateError ? 'Nelze vybrat tento den.' : editScheduleError}
                        </Form.Control.Feedback>                          
                      )} 
                    </FloatingLabel>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId="formStartTime">
                    <FloatingLabel label="čas začátku">
                      <Form.Select
                        name="startTime"
                        value={formData.startTime}
                        isInvalid={Boolean(editScheduleError)}
                        onChange={(e) => {
                          setEditScheduleError('')
                          const startTime = e.target.value
                          const minimumEndTime = getEndTimeFromStart(startTime)
                          const keepCurrentEnd = formData.endTime &&
                            startTime < formData.endTime &&
                            !clockRangeOverlapsBreak(startTime, formData.endTime)
                          setFormData(s => ({
                            ...s,
                            startTime,
                            endTime: keepCurrentEnd ? s.endTime : minimumEndTime
                          }))
                        }}
                        required
                      >
                        {timeOptions.slice(0, -1).map((time) => (
                          <option
                            key={time}
                            value={time}
                            disabled={clockRangeOverlapsBreak(time, getEndTimeFromStart(time))}
                          >
                            {time}
                          </option>
                        ))}
                      </Form.Select>
                      {editScheduleError ? <Form.Control.Feedback type="invalid">Změňte termín nebo servisní stání.</Form.Control.Feedback> : null}
                    </FloatingLabel>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId="formEndTime">
                    <FloatingLabel label="čas konce">
                      <Form.Select
                        name="endTime"
                        value={formData.endTime}
                        isInvalid={Boolean(editScheduleError)}
                        onChange={(e) => {
                          setEditScheduleError('')
                          const endTime = e.target.value
                          if (formData.startTime && endTime <= formData.startTime) {                              
                            const options = timeOptions
                            const endIndex = options.indexOf(endTime)
                            const newStartTime = options[endIndex - 1] || formData.startTime
                            setFormData(s => ({ ...s, startTime: newStartTime, endTime }))
                            return
                          }
                          setFormData(s => ({ ...s, endTime }))
                        }}
                        required                          
                      >
                        {timeOptions.slice(1).map((time) => (
                          <option
                            key={time}
                            value={time}
                            disabled={clockRangeOverlapsBreak(formData.startTime, time)}
                          >
                            {time}
                          </option>
                        ))}
                      </Form.Select>
                      {editScheduleError ? <Form.Control.Feedback type="invalid">Změňte termín nebo servisní stání.</Form.Control.Feedback> : null}
                    </FloatingLabel>
                  </Form.Group>
                </Col>
                <Col md={4}>
                <Form.Group controlId="formSpz" className='position-relative'>
                  <FloatingLabel label="spz">
                    <Form.Control
                      type="text"
                      name="spz"
                      value={formData.spz}
                      onChange={(e) => {
                        const safeSpz = normalizeSpzLocal(e.target.value)
                        setFormData((prev) => ({ ...prev, spz: safeSpz }))
                        handleEditSpzInput(safeSpz)
                      }}
                      placeholder="SPZ"
                      maxLength={12}
                      pattern="^[A-Za-z0-9]{5,9}$"
                      title="Zadejte SPZ bez mezer (5–9 znaků, písmena a čísla)"
                      onFocus={(e) => setTimeout(() => e.target.select(), 0)}
                        onBlur={(e) => {
                          const safeSpz = e.target.value
                            .replace(/\s+/g, '')
                            .toUpperCase()
                          setFormData((prev) => ({ ...prev, spz: safeSpz }))
                          setTimeout(() => setShowEditSpzHints(false), 120)
                          if (!safeSpz) {
                            clearEditPrefillFields()
                            setEditUschovaInfo(null)
                            setEditSpzHints([])
                            return
                          }
                          const selected = findSpzHintMatch(editSpzHints, safeSpz)
                          if (selected) {
                            applyStorageHintToEditForm(selected)
                            handleEditSpzLookup(selected.spz)
                          }
                        }}
                      />
                      <Form.Control.Feedback type="invalid">
                        Zadejte SPZ bez mezer (5–9 znaků, písmena a čísla)
                      </Form.Control.Feedback>
                    </FloatingLabel>
                    {showEditSpzHints && editSpzHints.length > 0 ? (
                      <div className='list-group position-absolute w-100 shadow-sm' style={{ zIndex: 20, top: '100%' }}>
                        {editSpzHints.map((item) => (
                          <button
                            key={`edit-spz-hint-${item.spz}`}
                            type="button"
                            className='list-group-item list-group-item-action py-2'
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleEditSpzPick(item)}
                          >
                            <div className='fw-semibold'>{item.spz}</div>
                            <div className='small text-muted'>{item.jmeno || '-'}</div>
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </Form.Group>
                </Col>  
                <Col md={4}>
                  <Form.Group controlId="formTypEdit">
                    <FloatingLabel label="typ vozidla">
                      <Form.Select
                        name="typ"
                        value={formData.typ || ''}
                        onChange={handleFormChange}
                      >
                        {Object.entries(VEHICLE_TYPES).map(([key, label]) => (
                          <option key={key} value={key}>{label}</option>
                        ))}
                      </Form.Select>
                    </FloatingLabel>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId="formZnackaEdit">
                    <FloatingLabel label="značka a model">
                      <Form.Control
                        type="text"
                        name='znacka'
                        value={formData.znacka || ''}
                        onChange={handleFormChange}
                        placeholder="značka a model"
                        maxLength={255}
                      />
                    </FloatingLabel>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="formUkonEdit">
                    <FloatingLabel label="servisní úkon">
                      <Form.Select
                        name="id_servisniho_ukonu"
                        value={formData.id_servisniho_ukonu || ''}
                        onChange={(e) => {
                          const value = e.target.value
                          setFormData((prev) => ({
                            ...prev,
                            id_servisniho_ukonu: value,
                            id_varianty_prezuti: Number(value) === 1 ? prev.id_varianty_prezuti : ''
                          }))
                        }}
                      >
                        <option value=''>Nevybráno</option>
                        {(reservationLookups?.servisniUkony || []).map((item) => (
                          <option key={item.id_ukonu} value={item.id_ukonu}>{item.nazev}</option>
                        ))}
                      </Form.Select>
                    </FloatingLabel>
                  </Form.Group>
                </Col>
                {Number(formData.id_servisniho_ukonu) === 1 ? (
                  <Col md={6}>
                    <Form.Group controlId="formVariantaPrezutiEdit">
                      <FloatingLabel label="varianta přezutí">
                        <Form.Select
                          name="id_varianty_prezuti"
                          value={formData.id_varianty_prezuti || ''}
                          onChange={handleFormChange}
                          required
                        >
                          <option value=''>Vyberte variantu</option>
                          {(reservationLookups?.variantyPrezuti || []).map((item) => (
                            <option key={item.id_varianty} value={item.id_varianty}>{item.nazev}</option>
                          ))}
                        </Form.Select>
                      </FloatingLabel>
                    </Form.Group>
                  </Col>
                ) : null}
                <Col xs={12}>
                  <Form.Group controlId="formPoznamka">
                    <FloatingLabel label="poznámka">
                      <Form.Control
                        as="textarea"
                        name="poznamka"
                        value={formData.poznamka}
                        onChange={handleFormChange}
                        style={{ height: '62px', minHeight: '62px', resize: 'none' }}
                        maxLength={256}
                      />
                    </FloatingLabel>
                  </Form.Group>
                </Col>  
                <Col md={4} className='reservation-contact-row'>
                <Form.Group controlId="formJmeno">
                  <FloatingLabel label="jméno">
                    <Form.Control
                      type="text"
                      name='jmeno'
                      value={formData.jmeno}
                      onChange={handleFormChange}
                      placeholder="Jméno"
                      maxLength={64}
                    />
                    </FloatingLabel>
                  </Form.Group>
                </Col>
                <Col md={4} className='reservation-contact-row'>
                  <Form.Group controlId="formFirmaEdit">
                    <FloatingLabel label="firma">
                      <Form.Control
                        type="text"
                        name='firma'
                        value={formData.firma || ''}
                        onChange={handleFormChange}
                        placeholder="Firma"
                        maxLength={150}
                      />
                    </FloatingLabel>
                  </Form.Group>
                </Col>
                <Col md={4} className='reservation-contact-row'>
                  <Form.Group controlId="formTelefon">                    
                    <InputGroup>
                      <FormSelect 
                        name="predvolba" 
                        value={formData.predvolba}
                        onChange={handleFormChange}
                        style={{ maxWidth: '100px' }}>
                        <option value="+420">+420</option>
                        <option value="+421">+421</option>
                        <option value="+48">+48</option>
                        <option value="+49">+49</option>
                        <option value="+43">+43</option>
                      </FormSelect>
                      <FloatingLabel label="telefon">
                        <FormControl
                          type="tel"
                          name='telefon'
                          value={formData.telefon}
                          onChange={handleFormChange}
                          placeholder="123456789"
                          pattern="^\d{9}$"
                          title="Zadejte 9 číslic bez mezer"
                          onFocus={(e) => setTimeout(() => e.target.select(), 0)}
                          onBlur={(e) => {
                            e.target.value = e.target.value
                              .replace(/\s+/g, '')
                          }}
                        />                             
                      </FloatingLabel>                      
                    </InputGroup>                                        
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId="formEmail">
                    <FloatingLabel label="email">
                      <Form.Control
                        type="email"
                        name='email'
                        value={formData.email}
                        onChange={handleFormChange}
                        placeholder="Email"
                        maxLength={64}
                      />
                    </FloatingLabel>
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group controlId="formStkEdit">
                    <FloatingLabel label="platnost STK">
                      <Form.Control type="date" name="stk" value={formData.stk || ''} onChange={handleFormChange} />
                    </FloatingLabel>
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group controlId="formTachometrEdit">
                    <FloatingLabel label="tachometr (km)">
                      <Form.Control
                        type="number"
                        min="0"
                        step="1"
                        name="tachometr"
                        value={formData.tachometr ?? ''}
                        onChange={handleFormChange}
                        placeholder="Tachometr"
                      />
                    </FloatingLabel>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId="formSkupinaEdit">
                    <FloatingLabel label="skupina zákazníka">
                      <Form.Select name="id_skupiny" value={formData.id_skupiny || 1} onChange={handleFormChange}>
                        {(reservationLookups?.skupiny || []).map((item) => (
                          <option key={item.id_skupiny} value={item.id_skupiny}>{item.nazev}</option>
                        ))}
                      </Form.Select>
                    </FloatingLabel>
                  </Form.Group>
                </Col>
                <Col xs={12}>
                  <div className='d-flex flex-wrap align-items-center gap-3 mt-3'>
                  <div className={`d-flex align-items-center gap-1 rounded ${editScheduleError ? 'border border-danger p-1' : ''}`}>
                  <span className='text-muted small flex-shrink-0'>stání</span>
                  <Form.Group controlId="formStani" className='text-muted'>
                    <ButtonGroup>
                      {Array.from({ length: STANI_COUNT }, (_, i) => (
                        <ToggleButton
                          key={`stani-${i + 1}`}
                          type="radio"
                          name="stani"
                          variant="outline-primary"
                          id={`stani-${i + 1}`}
                          value={i + 1}
                          checked={parseInt(formData.stani) === i + 1}
                          onChange={(e) => {
                            setEditScheduleError('')
                            const value = e.currentTarget.value
                            setFormData(prev => ({
                              ...prev,
                              stani: parseInt(value)
                            }))
                          }}
                          required
                        >
                          <>
                            <i className={`bi ${i + 1 === 3 ? 'bi-truck-front' : 'bi-car-front'} pe-2`}></i>
                            {i + 1}
                          </>
                        </ToggleButton>
                      ))}
                    </ButtonGroup>
                  </Form.Group>
                  </div>
                  {editScheduleError ? <div className='invalid-feedback d-block'>{editScheduleError}</div> : null}
                  </div>
                </Col>
              </Row>
                </Tab>
                <Tab
                  eventKey='uschova'
                  title={<><i className='bi bi-safe me-2'></i>Úschova <Badge bg='secondary' pill>{reservationDetail?.uschovy?.length || 0}</Badge></>}
                >
                  <div className='d-flex align-items-center justify-content-between mb-3'>
                    <div>
                      <h6 className='mb-1'>Navázané úschovy</h6>
                      <p className='text-muted small mb-0'>Záznamy jsou dohledané podle normalizované SPZ {formData.spz || '–'}.</p>
                    </div>
                  </div>
                  {(reservationDetail?.uschovy || []).length ? (
                    <div className='table-responsive rounded border'>
                      <Table hover className='align-middle mb-0 reservation-detail-table reservation-storage-table'>
                        <thead className='table-light'>
                          <tr>
                            <th>Zakázka</th>
                            <th>Příjem</th>
                            <th>Sezóna</th>
                            <th>Disk</th>
                            <th>Rozměr</th>
                            <th>Značka / model</th>
                            <th>Počet</th>
                            <th>Hloubka</th>
                            <th>TPMS</th>
                            <th>Stav</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reservationDetail.uschovy.map((item) => (
                            <tr key={item.id_zakazky}>
                              <td className='fw-semibold text-primary'>{item.cislo}</td>
                              <td>{formatDate(item.datum_prijmu) || '–'}</td>
                              <td>{STORAGE_SEASON[Number(item.sezona)] || '–'}</td>
                              <td>{STORAGE_DISK[Number(item.disk)] || '–'}</td>
                              <td>{item.rozmer || '–'}</td>
                              <td>{[item.znacka, item.model].filter(Boolean).join(' / ') || '–'}</td>
                              <td>{item.pocet || item.pocet_pneu || '–'}</td>
                              <td>{item.hloubka ? `${item.hloubka} mm` : '–'}</td>
                              <td>{Number(item.tmps) === 1 ? <i className='bi bi-check-circle-fill text-success'></i> : '–'}</td>
                              <td>
                                <Badge bg={Number(item.stav) === 1 ? 'success' : 'secondary'}>
                                  {Number(item.stav) === 1 ? 'uskladněno' : 'vydáno'}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <Alert variant='light' className='border mb-0 reservation-detail-empty-message'>
                      <i className='bi bi-info-circle me-2'></i>K této SPZ nebyla nalezena žádná úschova.
                    </Alert>
                  )}
                </Tab>
                <Tab
                  eventKey='historie'
                  title={<><i className='bi bi-clock-history me-2'></i>Historie změn <Badge bg='secondary' pill>{reservationHistory.length}</Badge></>}
                >
                  {reservationHistory.length ? (
                    <div className='table-responsive rounded border'>
                      <Table hover className='align-middle mb-0 reservation-detail-table reservation-history-table'>
                        <thead className='table-light'>
                          <tr>
                            <th>Datum</th>
                            <th>Uživatel</th>
                            <th>Typ</th>
                            <th>Pole</th>
                            <th>Původní hodnota</th>
                            <th>Nová hodnota</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reservationHistory.map((item) => (
                            <tr key={item.id_historie}>
                              <td className='text-nowrap'>{moment(item.datum_zmeny).format('D. M. YYYY HH:mm:ss')}</td>
                              <td>{item.uzivatel_jmeno || 'Systém'}</td>
                              <td><Badge bg='secondary'>{item.typ_zaznamu}</Badge></td>
                              <td>{item.pole || '–'}</td>
                              <td className='reservation-history-value'>{formatHistoryValue(item.pole, item.puvodni_hodnota)}</td>
                              <td className='reservation-history-value'>{formatHistoryValue(item.pole, item.nova_hodnota)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <Alert variant='light' className='border mb-0 reservation-detail-empty-message'>
                      <i className='bi bi-clock-history me-2'></i>Rezervace zatím nemá zaznamenané změny.
                    </Alert>
                  )}
                </Tab>
              </Tabs>
            </Modal.Body>
            <Modal.Footer className='border-0 d-flex justify-content-between px-3 pb-3'>
              <div>
              <Button variant="secondary me-1" onClick={() => closeModal()}><i className='bi bi-arrow-left me-2'></i>Zpět</Button>
              <Button variant="primary" type="submit"><i className='bi bi-save me-2'></i>{isCopyMode ? 'Uložit kopii' : 'Uložit'}</Button>
              </div>
            </Modal.Footer>
          </Form>
        </Modal>
      )}

      <Notification notifications={notifications} setNotifications={setNotifications} />       
    </>    
  )
}

export default Reservations


